import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useProject } from '@/contexts/ProjectContext';
import AIAnalystPanel from '@/components/ai/AIAnalystPanel';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  ArrowLeft, RefreshCw, Loader2, Grid3X3, ChevronDown, ChevronRight, Download, Upload,
  AlertTriangle, Eye, DollarSign, TrendingUp, BarChart3, Target,
  CheckCircle2, XCircle, Minus, Calculator, Layers, X, Sliders, Wand2, Save,
  RotateCcw, Info, Plus, Trash2, FileSpreadsheet
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AgGridReact } from 'ag-grid-react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Legend
} from 'recharts';

function fmt(val) {
  if (val === undefined || val === null || val === 0) return '0';
  if (Math.abs(val) >= 10000000) return `${(val / 10000000).toFixed(2)} Cr`;
  if (Math.abs(val) >= 100000) return `${(val / 100000).toFixed(1)} L`;
  return val.toLocaleString('en-IN', { maximumFractionDigits: 0 });
}
function fmtINR(val) {
  if (val === undefined || val === null) return '';
  return val.toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

const VIEW_MODES = [
  { id: 'variance', label: 'Variance View', icon: Target },
  { id: 'budget', label: 'Budget View', icon: DollarSign },
  { id: 'actual', label: 'Actual View', icon: Eye },
  { id: 'forecast', label: 'Forecast View', icon: TrendingUp },
  { id: 'commitment', label: 'Commitment View', icon: BarChart3 },
];

const EPC_TEMPLATES = {
  ENGINEERING:        { MAN: 55, MAT: 0, EQP: 3, SUB: 30, SVC: 7, OVH: 5 },
  PROCUREMENT:        { MAN: 2, MAT: 5, EQP: 0, SUB: 85, SVC: 3, OVH: 5 },
  CONSTRUCTION:       { MAN: 42, MAT: 8, EQP: 12, SUB: 30, SVC: 5, OVH: 3 },
  PRE_COMMISSIONING:  { MAN: 50, MAT: 5, EQP: 10, SUB: 25, SVC: 8, OVH: 2 },
  PROJECT_MANAGEMENT: { MAN: 65, MAT: 2, EQP: 3, SUB: 5, SVC: 5, OVH: 20 },
  CONTINGENCY:        { MAN: 30, MAT: 10, EQP: 10, SUB: 30, SVC: 10, OVH: 10 },
};

function KPICard({ title, value, subtitle, icon: Icon, color = '#0EA5E9' }) {
  return (
    <div className="rounded-xl p-4" data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}
      style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
      <div className="flex items-start justify-between mb-2">
        <div className="p-2 rounded-lg" style={{ background: `${color}12`, border: `1px solid ${color}20` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
      </div>
      <div className="text-2xl font-bold mb-1" style={{ color: 'var(--text-primary)' }}>{value}</div>
      <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{title}</div>
      {subtitle && <div className="text-[10px] mt-1" style={{ color: 'var(--text-disabled)' }}>{subtitle}</div>}
    </div>
  );
}

// ── Currency helpers (project base + live FX conversion) ─────────────────
const CURRENCY_SYMBOL = {
  INR: '₹', USD: '$', EUR: '€', GBP: '£', AED: 'د.إ', SAR: '﷼', QAR: 'ر.ق',
  KWD: 'د.ك', OMR: 'ر.ع', BHD: '.د.ب', JPY: '¥', CNY: '¥', SGD: 'S$', AUD: 'A$',
  CAD: 'C$', CHF: 'CHF', HKD: 'HK$', ZAR: 'R',
};
const CURRENCY_LOCALE = { INR: 'en-IN', USD: 'en-US', EUR: 'de-DE', GBP: 'en-GB',
  AED: 'en-AE', SAR: 'en-SA', JPY: 'ja-JP', CNY: 'zh-CN', SGD: 'en-SG', AUD: 'en-AU',
  CAD: 'en-CA', CHF: 'de-CH', HKD: 'en-HK', ZAR: 'en-ZA' };
const currencySymbol = (c) => CURRENCY_SYMBOL[c] || (c ? `${c} ` : '₹');
const SUPPORTED_CCY = ['INR','USD','EUR','GBP','AED','SAR','QAR','KWD','OMR','BHD','JPY','CNY','SGD','AUD','CAD','CHF','HKD','ZAR'];


/* ═══════════════════════════════════════════════════════
   BUDGET DISTRIBUTION WIZARD
   ═══════════════════════════════════════════════════════ */
function BudgetDistributionWizard({ isOpen, onClose, costCodeRow, elements, selectedProject, onDistributed, baseCurrency = 'INR', fxRates = {} }) {
  const [method, setMethod] = useState('manual'); // manual | template | current
  const [allocs, setAllocs] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [elementsData, setElementsData] = useState([]);
  // Cognara-suggested sub-levers for the current cost code
  const [cognaraSuggesting, setCognaraSuggesting] = useState(false);
  const [cognaraLevers, setCognaraLevers] = useState([]);
  // Editable local override for the row's currentBudget — lets planners
  // stage the split (Cognara or manual) BEFORE budget is finalised, then
  // watch the amounts auto-compute the moment they set a real number.
  const [localBudget, setLocalBudget] = useState(0);
  // Cognara-lever templates saved by name for re-use across sibling activities.
  // Persisted per-project in localStorage so a browser refresh doesn't lose them.
  const [cognaraTemplates, setCognaraTemplates] = useState([]);

  // Fetch full element details (with rate/UOM info) + shared Cognara templates.
  useEffect(() => {
    if (!isOpen || !selectedProject) return;
    api.get(`/v1/projects/${selectedProject}/cc/cost-elements?flat=true`).then(({ data }) => {
      setElementsData((data.elements || []).filter(e => e.level === 0));
    }).catch(() => {});
    // Server-first: fetch shared templates so every teammate on the project
    // sees the same list. Fall back to the localStorage cache when offline.
    (async () => {
      try {
        const { data } = await api.get(`/v1/projects/${selectedProject}/cc/cbs/lever-templates`);
        setCognaraTemplates(data.templates || []);
        try { localStorage.setItem(`cognara-templates:${selectedProject}`, JSON.stringify(data.templates || [])); } catch {}
      } catch {
        try {
          const raw = localStorage.getItem(`cognara-templates:${selectedProject}`);
          setCognaraTemplates(raw ? JSON.parse(raw) : []);
        } catch { setCognaraTemplates([]); }
      }
    })();
  }, [isOpen, selectedProject]);

  const budget = localBudget || costCodeRow?.currentBudget || costCodeRow?.totalBudget || 0;
  const category = costCodeRow?.costCategory || 'CONSTRUCTION';

  // Keep localBudget in sync when the row switches
  useEffect(() => {
    if (!isOpen) return;
    setLocalBudget(Number(costCodeRow?.currentBudget) || Number(costCodeRow?.totalBudget) || 0);
  }, [isOpen, costCodeRow]);

  // Auto-reload allocations when `budget` changes: keep every element's
  // staged `pct` and recompute `amount = pct × budget`. This lets planners
  // apply Cognara levers at budget=0 and just type a real budget to see
  // the amounts populate without re-opening the dialog.
  useEffect(() => {
    setAllocs(prev => {
      if (!prev || !Object.keys(prev).length) return prev;
      const next = {};
      for (const [k, v] of Object.entries(prev)) {
        const pct = Number(v?.pct) || 0;
        next[k] = { ...v, amount: Math.round((pct / 100) * budget) };
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budget]);

  // When the parent doesn't pass a populated `elements` prop (common for
  // fresh rows that haven't been distributed yet), fall back to the flat
  // elementsData fetched from `/cc/cost-elements`. Guarantees the Cost
  // Element table always renders once the dialog opens — otherwise the
  // body stays blank and users can't see where their staged Cognara
  // levers landed. Declared BEFORE the initialisation effect so the
  // effect can reference it safely (avoids temporal-dead-zone crash).
  const displayElements = useMemo(() => (
    (Array.isArray(elements) && elements.length > 0) ? elements : elementsData
  ), [elements, elementsData]);

  // Initialize allocations
  useEffect(() => {
    if (!isOpen || !elements?.length) return;
    const init = {};
    (displayElements || elements).forEach(el => {
      const existing = costCodeRow?.cells?.[el.elementCode];
      init[el.elementCode] = {
        amount: existing?.budget || 0,
        pct: budget > 0 ? ((existing?.budget || 0) / budget) * 100 : 0,
        qty: null,
        rate: null,
        subs: [],           // sub-breakdown rows for this element
        subsOpen: false,    // accordion state
      };
    });
    setAllocs(init);
    setMethod('current');
  }, [isOpen, elements, elementsData, costCodeRow, budget, displayElements]);


  const totalAllocated = Object.values(allocs).reduce((s, a) => s + (a.amount || 0), 0);
  const difference = totalAllocated - budget;
  const isBalanced = Math.abs(difference) < 1;
  const diffPct = budget > 0 ? (difference / budget) * 100 : 0;

  const updateAmount = (code, amount) => {
    const val = parseFloat(amount) || 0;
    setAllocs(prev => ({
      ...prev,
      [code]: { ...prev[code], amount: val, pct: budget > 0 ? (val / budget) * 100 : 0 }
    }));
  };

  const updatePct = (code, pct) => {
    const val = parseFloat(pct) || 0;
    setAllocs(prev => {
      // Cap the *cumulative* pct at 100 so planners can't over-allocate.
      const otherPct = Object.entries(prev)
        .filter(([k]) => k !== code)
        .reduce((s, [, a]) => s + (Number(a?.pct) || 0), 0);
      const capped = Math.max(0, Math.min(val, Math.max(0, 100 - otherPct)));
      if (val > capped) toast.warning(`Capped at ${capped.toFixed(1)}% — remaining headroom before hitting 100%`);
      return {
        ...prev,
        [code]: { ...prev[code], pct: capped, amount: Math.round((capped / 100) * budget) }
      };
    });
  };

  const updateSlider = (code, pct) => {
    updatePct(code, pct);
  };

  const updateQty = (code, qty) => {
    setAllocs(prev => ({ ...prev, [code]: { ...prev[code], qty: parseFloat(qty) || null } }));
  };
  const updateRate = (code, rate) => {
    setAllocs(prev => ({ ...prev, [code]: { ...prev[code], rate: parseFloat(rate) || null } }));
  };

  const applyTemplate = () => {
    const ratios = EPC_TEMPLATES[category] || EPC_TEMPLATES.CONSTRUCTION;
    const newAllocs = {};
    // Only distribute to elements that have a ratio in the template
    const activeElements = elements.filter(e => (ratios[e.elementCode] || 0) > 0);
    let running = 0;
    elements.forEach(el => {
      const pct = ratios[el.elementCode] || 0;
      if (pct <= 0) {
        newAllocs[el.elementCode] = { amount: 0, pct: 0, qty: null, rate: null };
        return;
      }
      const isLast = el.elementCode === activeElements[activeElements.length - 1]?.elementCode;
      const amt = isLast ? budget - running : Math.round((pct / 100) * budget);
      running += amt;
      newAllocs[el.elementCode] = { amount: amt, pct: budget > 0 ? (amt / budget) * 100 : 0, qty: null, rate: null };
    });
    setAllocs(newAllocs);
    setMethod('template');
    toast.success(`Applied ${category.replace(/_/g, ' ')} template ratios`);
  };

  const resetToZero = () => {
    const z = {};
    elements.forEach(el => { z[el.elementCode] = { amount: 0, pct: 0, qty: null, rate: null }; });
    setAllocs(z);
    setMethod('manual');
  };

  const autoBalance = () => {
    if (isBalanced) return;
    const codes = elements.map(e => e.elementCode).filter(c => (allocs[c]?.amount || 0) > 0);
    if (codes.length === 0) return;
    // Distribute the difference proportionally
    const newAllocs = { ...allocs };
    const total = codes.reduce((s, c) => s + (newAllocs[c]?.amount || 0), 0);
    if (total === 0) return;
    let runningTotal = 0;
    codes.forEach((code, idx) => {
      const proportion = (newAllocs[code]?.amount || 0) / total;
      const newAmt = idx === codes.length - 1
        ? budget - runningTotal
        : Math.round(proportion * budget);
      runningTotal += newAmt;
      newAllocs[code] = { ...newAllocs[code], amount: newAmt, pct: budget > 0 ? (newAmt / budget) * 100 : 0 };
    });
    setAllocs(newAllocs);
    toast.success('Budget auto-balanced');
  };

  const handleSubmit = async () => {
    if (!isBalanced) {
      toast.error('Distribution must exactly match the cost code budget');
      return;
    }
    setSubmitting(true);
    try {
      const distributions = elements
        .filter(el => (allocs[el.elementCode]?.amount || 0) > 0)
        .map(el => {
          const a = allocs[el.elementCode];
          const obj = { elementCode: el.elementCode, amount: a.amount };
          if (a.qty) obj.qty = a.qty;
          if (a.rate) obj.rate = a.rate;
          const elData = elementsData.find(e => e.elementCode === el.elementCode);
          if (a.qty && !a.rate && elData?.defaultRate) obj.rate = elData.defaultRate;
          if (elData?.defaultUOM) obj.uom = elData.defaultUOM;
          return obj;
        });
      await api.post(`/v1/projects/${selectedProject}/cc/cbs/distribute`, {
        costCode: costCodeRow.costCode,
        distributions,
      });
      toast.success(`Budget distributed for ${costCodeRow.costCode} — ${costCodeRow.costCodeName}`);
      onDistributed();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Distribution failed');
    } finally { setSubmitting(false); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="distribute-wizard-overlay">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      {/* Dialog */}
      <div className="relative w-[1200px] max-w-[95vw] max-h-[90vh] rounded-2xl overflow-hidden flex flex-col"
        style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', boxShadow: '0 25px 60px rgba(0,0,0,0.3)' }}
        data-testid="distribute-wizard-dialog">

        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border-color)' }}>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg" style={{ background: '#6366F115', border: '1px solid #6366F130' }}>
              <Sliders className="w-5 h-5" style={{ color: '#6366F1' }} />
            </div>
            <div>
              <h2 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>Distribute Budget</h2>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                {costCodeRow?.costCode} — {costCodeRow?.costCodeName}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:opacity-70" data-testid="wizard-close-btn">
            <X className="w-5 h-5" style={{ color: 'var(--text-muted)' }} />
          </button>
        </div>

        {/* Budget bar */}
        <div className="px-6 py-3 flex items-center justify-between" style={{ backgroundColor: 'var(--table-header-bg)' }}>
          <div className="flex items-center gap-6">
            <div>
              <div className="text-[10px] uppercase font-semibold tracking-wider" style={{ color: 'var(--text-disabled)' }}>Current Budget</div>
              {/* Inline editable — used to auto-reload amounts once staged
                  Cognara levers exist and the planner types a real number. */}
              <input
                type="number"
                value={localBudget || ''}
                min={0}
                onChange={(e) => setLocalBudget(parseFloat(e.target.value) || 0)}
                placeholder={fmtINR(costCodeRow?.currentBudget || 0)}
                className="text-lg font-bold bg-transparent border-b border-transparent hover:border-[var(--border-color)] focus:border-[var(--accent-primary)] focus:outline-none w-40"
                style={{ color: 'var(--text-primary)' }}
                data-testid="row-budget-input"
                title="Type a budget — amounts auto-compute from the staged split"
              />
            </div>
            <div>
              <div className="text-[10px] uppercase font-semibold tracking-wider" style={{ color: 'var(--text-disabled)' }}>Category</div>
              <div className="text-sm font-medium" style={{ color: '#6366F1' }}>{category.replace(/_/g, ' ')}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => {
              // If saved Cognara templates exist for this project, offer them
              // first — otherwise fall back to the legacy static template.
              if (cognaraTemplates.length > 0) {
                const pick = window.prompt(
                  `Apply which Cognara template?  (type the number, or "d N" to delete row N)\n\n${cognaraTemplates.map((t, i) => `${i + 1}. ${t.name}`).join('\n')}`,
                  '1',
                );
                if (!pick) return;
                const delMatch = /^d\s+(\d+)$/i.exec(pick.trim());
                if (delMatch) {
                  const dIdx = parseInt(delMatch[1], 10) - 1;
                  const victim = cognaraTemplates[dIdx];
                  if (!victim) return;
                  api.delete(`/v1/projects/${selectedProject}/cc/cbs/lever-templates/${victim.id}`)
                    .then(() => {
                      const next = cognaraTemplates.filter((_, i) => i !== dIdx);
                      setCognaraTemplates(next);
                      try { localStorage.setItem(`cognara-templates:${selectedProject}`, JSON.stringify(next)); } catch {}
                      toast.success(`Deleted template "${victim.name}"`);
                    })
                    .catch(() => toast.error('Delete failed'));
                  return;
                }
                const idx = parseInt(pick, 10) - 1;
                if (idx >= 0 && idx < cognaraTemplates.length) {
                  setCognaraLevers(cognaraTemplates[idx].levers || []);
                  toast.success(`Applied template "${cognaraTemplates[idx].name}" — click Apply on each lever`);
                  return;
                }
              }
              applyTemplate();
            }} data-testid="apply-template-btn">
              <Wand2 className="w-3.5 h-3.5 mr-1" /> Apply Template{cognaraTemplates.length > 0 ? ` (${cognaraTemplates.length})` : ''}
            </Button>
            <Button variant="outline" size="sm" onClick={autoBalance} disabled={isBalanced} data-testid="auto-balance-btn">
              <Calculator className="w-3.5 h-3.5 mr-1" /> Auto-Balance
            </Button>
            <Button variant="outline" size="sm" onClick={resetToZero} data-testid="reset-btn">
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset
            </Button>
            <Button variant="default" size="sm" disabled={cognaraSuggesting} data-testid="cognara-suggest-btn"
              className="bg-purple-600 hover:bg-purple-700 text-white"
              onClick={async () => {
                setCognaraSuggesting(true);
                try {
                  const r = await api.post(`/v1/projects/${selectedProject}/cc/cbs/suggest-levers`, {
                    costCode: costCodeRow?.costCode,
                    costCodeName: costCodeRow?.costCodeName,
                    costCategory: costCodeRow?.costCategory,
                  });
                  setCognaraLevers(r.data.levers || []);
                  toast.success(`Cognara suggested ${r.data.levers?.length || 0} sub-levers`);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Cognara suggest failed');
                } finally { setCognaraSuggesting(false); }
              }}>
              {cognaraSuggesting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1" />}
              Suggest with Cognara
            </Button>
          </div>
        </div>

        {/* Cognara-suggested sub-levers panel */}
        {cognaraLevers.length > 0 && (
          <div className="px-6 py-3" style={{ backgroundColor: 'rgba(139,92,246,0.04)', borderBottom: '1px solid var(--border-color)' }} data-testid="cognara-levers-panel">
            <div className="flex items-center gap-2 mb-2">
              <Wand2 className="w-3.5 h-3.5" style={{ color: '#8B5CF6' }} />
              <span className="text-xs font-semibold" style={{ color: '#8B5CF6' }}>Cognara Suggested Sub-Levers</span>
              <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                Click "Apply" to distribute this activity's total across these levers
              </span>
              {/* Save-as-Template — one-click store of the current levers
                  under a name; a sibling activity can then "Apply Template"
                  without pinging Cognara again. */}
              <button
                onClick={async () => {
                  const name = window.prompt(
                    'Name this Cognara lever template',
                    `${costCodeRow?.costCategory || 'DIRECT'} — ${(costCodeRow?.costCodeName || 'template').slice(0, 40)}`,
                  );
                  if (!name) return;
                  try {
                    // Server-first: teammates on the same project immediately
                    // see the new template on their next open.
                    const { data } = await api.post(
                      `/v1/projects/${selectedProject}/cc/cbs/lever-templates`,
                      { name, category: costCodeRow?.costCategory || 'DIRECT', levers: cognaraLevers },
                    );
                    const saved = data.template;
                    const next = [saved, ...cognaraTemplates.filter(t => t.name !== name)].slice(0, 50);
                    setCognaraTemplates(next);
                    try { localStorage.setItem(`cognara-templates:${selectedProject}`, JSON.stringify(next)); } catch {}
                    toast.success(`Saved "${name}" — shared with everyone on this project`);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Could not save template');
                  }
                }}
                className="ml-auto text-[10px] px-2 py-0.5 rounded"
                style={{ backgroundColor: '#8B5CF6', color: '#fff' }}
                data-testid="save-lever-template-btn"
                title="Save current lever mix as a reusable template"
              >
                <Save className="w-3 h-3 inline mr-1" />Save as Template
              </button>
              <button onClick={() => setCognaraLevers([])} className="text-xs" style={{ color: 'var(--text-muted)' }}>Dismiss</button>
            </div>
            {/* Apply-All — one-click fan-out of every suggested lever to
                its parent element. Each parent element receives the SUM of
                its children shares × budget. Overwrites any staged split so
                the planner sees a clean canvas post-apply. */}
            <div className="flex items-center gap-2 mb-2">
              <button
                onClick={() => {
                  const total = Number(costCodeRow?.currentBudget) || 0;
                  const parentShares = {};
                  cognaraLevers.forEach(l => {
                    const p = (l.parentCode || (l.code || '').split('.')[0] || 'MAN').toUpperCase();
                    parentShares[p] = (parentShares[p] || 0) + (Number(l.share) || 0);
                  });
                  const totalShare = Object.values(parentShares).reduce((s, v) => s + v, 0) || 1;
                  setAllocs(prev => {
                    const next = { ...prev };
                    Object.entries(parentShares).forEach(([parent, share]) => {
                      const pct = Math.round((share / totalShare) * 100 * 10) / 10;
                      const amount = Math.round(total * (share / totalShare));
                      next[parent] = { ...(next[parent] || {}), amount, pct };
                    });
                    return next;
                  });
                  toast.success(`Applied ${cognaraLevers.length} levers across ${Object.keys(parentShares).length} elements`);
                }}
                className="text-[11px] px-2 py-1 rounded font-semibold"
                style={{ backgroundColor: '#8B5CF6', color: '#fff' }}
                data-testid="cognara-apply-all-btn"
              >
                <Wand2 className="w-3 h-3 inline mr-1" />Apply All ({cognaraLevers.length}) to Elements
              </button>
              <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                Fans every lever out to its parent (MAN/MAT/EQP/SUB/OVH) at once
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {cognaraLevers.map((l, i) => {
                const parent = (l.parentCode || (l.code || '').split('.')[0] || 'MAN').toUpperCase();
                const parentEl = displayElements.find(e => e.elementCode === parent);
                const parentColor = parentEl?.displayColor || '#8B5CF6';
                return (
                <div key={i} className="flex items-center gap-2 px-2 py-1 rounded text-xs" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid={`cognara-lever-${l.code}`}>
                  <span className="font-mono text-[10px]" style={{ color: '#8B5CF6' }}>{l.code}</span>
                  <span className="flex-1 truncate" style={{ color: 'var(--text-primary)' }}>{l.name}</span>
                  {/* Target-element chip so planners see WHERE each lever lands. */}
                  <span
                    className="text-[9px] font-mono px-1.5 py-0.5 rounded shrink-0"
                    style={{ backgroundColor: `${parentColor}22`, color: parentColor, border: `1px solid ${parentColor}55` }}
                    title={`Lands on ${parentEl?.elementName || parent}`}
                  >
                    → {parentEl?.shortName || parent}
                  </span>
                  <span className="font-mono text-[10px]" style={{ color: 'var(--text-muted)' }}>{(l.share * 100).toFixed(1)}%</span>
                  <button
                    onClick={() => {
                      // Distribute this lever's share into its parent
                      // element (MAN/MAT/EQP/SVC/OVH). Works even when
                      // `currentBudget` is 0 — in that case we bank the
                      // percentage so the split is preserved and the
                      // amount auto-computes the moment a budget lands
                      // on this row.
                      const total = Number(costCodeRow?.currentBudget) || 0;
                      const parent = l.parentCode || l.code?.split('.')[0] || 'MAN';
                      const addPct = Number(l.share || 0) * 100;
                      const addAmt = Math.round(total * (Number(l.share) || 0));
                      setAllocs(prev => {
                        const prior = prev[parent] || { amount: 0, pct: 0 };
                        const nextPct = Math.min(100, (Number(prior.pct) || 0) + addPct);
                        const nextAmt = (Number(prior.amount) || 0) + addAmt;
                        return {
                          ...prev,
                          [parent]: {
                            amount: total > 0 ? nextAmt : Math.round(total * (nextPct / 100)),
                            pct: total > 0 ? Math.round((nextAmt / total) * 100 * 10) / 10 : Math.round(nextPct * 10) / 10,
                          },
                        };
                      });
                      if (total <= 0) {
                        toast.success(`Cognara lever staged (${addPct.toFixed(1)}% → ${parent}) — amount will compute once budget is set on this row`);
                      } else {
                        toast.success(`Applied ${addPct.toFixed(1)}% (₹${addAmt.toLocaleString('en-IN')}) → ${parent}`);
                      }
                    }}
                    className="text-[10px] px-1.5 py-0.5 rounded"
                    style={{ backgroundColor: '#8B5CF6', color: '#fff' }}
                    data-testid={`apply-lever-${l.code}`}>
                    Apply
                  </button>
                </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Element rows */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2" data-testid="element-rows">
          {/* Header */}
          <div className="grid grid-cols-[220px_1fr_130px_170px_80px_100px_100px] gap-3 text-[10px] font-semibold uppercase tracking-wider pb-2"
            style={{ color: 'var(--text-disabled)', borderBottom: '1px solid var(--border-color)' }}>
            <div>Cost Element</div>
            <div>Distribution</div>
            <div className="text-right">Amount</div>
            <div className="text-right">Actual (FX aware)</div>
            <div className="text-right">%</div>
            <div className="text-right">Qty Budget</div>
            <div className="text-right">Rate/Unit</div>
          </div>
          {displayElements.map(el => {
            const a = allocs[el.elementCode] || { amount: 0, pct: 0, subs: [], subsOpen: false };
            const elData = elementsData.find(e => e.elementCode === el.elementCode);
            const showQty = el.elementCode === 'MAN' || el.elementCode === 'EQP';
            const sliderPct = Math.min(a.pct || 0, 100);
            const subs = Array.isArray(a.subs) ? a.subs : [];
            const subsBaseTotal = subs.reduce((s, r) => s + (Number(r.baseAmount) || 0), 0);
            return (
              <div key={el.elementCode} className="rounded-lg transition-colors"
                style={{ backgroundColor: (a.amount || 0) > 0 ? `${el.displayColor}06` : 'transparent' }}
                data-testid={`element-row-${el.elementCode}`}>
              <div className="grid grid-cols-[220px_1fr_130px_170px_80px_100px_100px] gap-3 items-center py-2 px-2">
                {/* Element label */}
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: el.displayColor }} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                      {el.elementName || el.shortName}
                    </div>
                    <div className="text-[10px] flex items-center gap-1" style={{ color: 'var(--text-disabled)' }}>
                      {el.elementCode}{elData?.defaultUOM ? ` | ${elData.defaultUOM}` : ''}
                      {/* Sub-breakdown accordion toggle */}
                      <button
                        onClick={() => setAllocs(prev => ({ ...prev, [el.elementCode]: { ...(prev[el.elementCode] || {}), subsOpen: !prev[el.elementCode]?.subsOpen } }))}
                        className="ml-1 text-[10px] px-1 py-0.5 rounded hover:opacity-80 shrink-0"
                        style={{ backgroundColor: subs.length ? '#8B5CF622' : 'transparent', color: subs.length ? '#8B5CF6' : 'var(--text-muted)' }}
                        title="Toggle sub-breakdown (add sub-categories with different currencies)"
                        data-testid={`toggle-subs-${el.elementCode}`}
                      >
                        {a.subsOpen ? <ChevronDown className="w-3 h-3 inline" /> : <ChevronRight className="w-3 h-3 inline" />}
                        {subs.length > 0 ? `${subs.length}` : 'break down'}
                      </button>
                    </div>
                  </div>
                </div>
                {/* Slider */}
                <div className="relative h-8 flex items-center">
                  <div className="absolute inset-x-0 h-2 rounded-full" style={{ backgroundColor: 'var(--border-color)' }} />
                  <div className="absolute left-0 h-2 rounded-full transition-all duration-150" style={{ width: `${sliderPct}%`, backgroundColor: el.displayColor }} />
                  <input
                    type="range" min="0" max="100" step="0.5"
                    value={sliderPct}
                    onChange={e => updateSlider(el.elementCode, parseFloat(e.target.value))}
                    className="absolute inset-x-0 h-8 opacity-0 cursor-pointer"
                    data-testid={`slider-${el.elementCode}`}
                  />
                  <div className="absolute h-4 w-4 rounded-full border-2 shadow-md transition-all duration-150 pointer-events-none"
                    style={{ left: `calc(${sliderPct}% - 8px)`, backgroundColor: 'white', borderColor: el.displayColor }} />
                </div>
                {/* Amount input */}
                <input
                  type="text"
                  value={a.amount ? fmtINR(Math.round(a.amount)) : ''}
                  onChange={e => {
                    const raw = e.target.value.replace(/,/g, '');
                    updateAmount(el.elementCode, raw);
                  }}
                  placeholder="0"
                  className="text-right text-sm font-mono rounded-lg px-2 py-1.5 w-full"
                  style={{ backgroundColor: 'var(--card-bg-alt)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                  data-testid={`amount-input-${el.elementCode}`}
                />
                {/* Actual input — inline accrual entry so planners can
                    log actual spend against this lever without leaving
                    the wizard. Supports FOREIGN-CURRENCY entry: pick the
                    currency at right and we auto-convert to project base
                    using live FX rates. Persists on blur. */}
                <div className="flex items-center gap-1">
                  <input
                    type="text"
                    defaultValue={a.actual ? fmtINR(Math.round(a.actual)) : ''}
                    onBlur={async (e) => {
                      const raw = parseFloat(e.target.value.replace(/,/g, '')) || 0;
                      const ccy = (a.actualCcy || baseCurrency).toUpperCase();
                      // Convert to base if foreign. `fxRates[X] = 1 base → X foreign`,
                      // so `base = foreign / fxRates[X]`.
                      const rate = fxRates?.[ccy];
                      const converted = (ccy === baseCurrency || !rate) ? raw : raw / rate;
                      const rounded = Math.round(converted);
                      setAllocs(prev => ({ ...prev, [el.elementCode]: { ...(prev[el.elementCode] || {}), actual: rounded, actualForeign: ccy !== baseCurrency ? raw : null, actualCcy: ccy } }));
                      try {
                        await api.put(
                          `/v1/projects/${selectedProject}/cc/cost-codes/${costCodeRow?.costCode}/actual`,
                          { elementCode: el.elementCode, actual: rounded, foreignAmount: ccy !== baseCurrency ? raw : null, foreignCurrency: ccy, fxRate: rate || 1 },
                        );
                        if (ccy !== baseCurrency && rate) {
                          toast.success(`Actual: ${currencySymbol(ccy)}${raw.toLocaleString()} @ ${(1/rate).toFixed(4)} → ${currencySymbol(baseCurrency)}${rounded.toLocaleString()} → ${el.elementCode}`);
                        } else {
                          toast.success(`Actual saved: ${currencySymbol(baseCurrency)}${rounded.toLocaleString()} → ${el.elementCode}`);
                        }
                      } catch { /* endpoint may accept later — non-blocking */ }
                    }}
                    placeholder={`Actual ${currencySymbol(a.actualCcy || baseCurrency)}`}
                    className="text-right text-xs font-mono rounded-lg px-2 py-1.5 flex-1 min-w-0"
                    style={{ backgroundColor: 'var(--card-bg-alt)', border: '1px dashed var(--border-color)', color: 'var(--text-secondary)' }}
                    data-testid={`actual-input-${el.elementCode}`}
                    title={a.actualCcy && a.actualCcy !== baseCurrency && fxRates?.[a.actualCcy]
                      ? `Foreign: ${currencySymbol(a.actualCcy)}${(a.actualForeign || 0).toLocaleString()} · 1 ${a.actualCcy} = ${(1/fxRates[a.actualCcy]).toFixed(4)} ${baseCurrency}`
                      : 'Log actual cost accrued against this lever — pick foreign currency at right to auto-convert'}
                  />
                  <select
                    value={a.actualCcy || baseCurrency}
                    onChange={e => setAllocs(prev => ({ ...prev, [el.elementCode]: { ...(prev[el.elementCode] || {}), actualCcy: e.target.value } }))}
                    className="text-[10px] font-mono rounded px-1 py-1.5 shrink-0"
                    style={{ backgroundColor: 'var(--card-bg-alt)', border: '1px dashed var(--border-color)', color: 'var(--text-secondary)' }}
                    data-testid={`actual-ccy-${el.elementCode}`}
                    title="Currency of this actual — auto-converts to project base"
                  >
                    {SUPPORTED_CCY.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                {/* FX conversion hint — visible only when foreign currency selected. */}
                {a.actualCcy && a.actualCcy !== baseCurrency && fxRates?.[a.actualCcy] && (
                  <div className="col-span-full -mt-1 text-[9px] font-mono pl-2" style={{ color: '#8B5CF6' }} data-testid={`fx-hint-${el.elementCode}`}>
                    1 {a.actualCcy} = {(1 / fxRates[a.actualCcy]).toFixed(4)} {baseCurrency}
                    {a.actual ? ` · saved as ${currencySymbol(baseCurrency)}${a.actual.toLocaleString()}` : ''}
                  </div>
                )}
                {/* Percentage */}
                <div className="relative">
                  <input
                    type="number" min="0" max="100" step="0.1"
                    value={a.pct ? a.pct.toFixed(1) : ''}
                    onChange={e => updatePct(el.elementCode, e.target.value)}
                    placeholder="0"
                    className="text-right text-sm font-mono rounded-lg px-2 py-1.5 w-full pr-6"
                    style={{ backgroundColor: 'var(--input-bg, var(--card-bg))', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                    data-testid={`pct-${el.elementCode}`}
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs" style={{ color: 'var(--text-disabled)' }}>%</span>
                </div>
                {/* Qty */}
                {showQty ? (
                  <input
                    type="number" min="0" step="1"
                    value={a.qty || ''}
                    onChange={e => updateQty(el.elementCode, e.target.value)}
                    placeholder={elData?.defaultUOM === 'MANHOUR' ? 'MH' : 'HR'}
                    className="text-right text-sm font-mono rounded-lg px-2 py-1.5 w-full"
                    style={{ backgroundColor: 'var(--input-bg, var(--card-bg))', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                    data-testid={`qty-${el.elementCode}`}
                  />
                ) : (
                  <div className="text-center text-xs" style={{ color: 'var(--text-disabled)' }}>—</div>
                )}
                {/* Rate */}
                {showQty ? (
                  <input
                    type="number" min="0" step="1"
                    value={a.rate || (elData?.defaultRate || '')}
                    onChange={e => updateRate(el.elementCode, e.target.value)}
                    placeholder={elData?.defaultRate ? `${elData.defaultRate}` : ''}
                    className="text-right text-sm font-mono rounded-lg px-2 py-1.5 w-full"
                    style={{ backgroundColor: 'var(--input-bg, var(--card-bg))', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                    data-testid={`rate-${el.elementCode}`}
                  />
                ) : (
                  <div className="text-center text-xs" style={{ color: 'var(--text-disabled)' }}>—</div>
                )}
              </div>
              {/* Sub-breakdown accordion — add per-element sub-categories
                  each with its own currency; amounts auto-convert via live
                  FX and roll up into the parent element amount. */}
              {a.subsOpen && (
                <div className="mx-4 mb-2 rounded-lg" style={{ backgroundColor: 'rgba(139,92,246,0.04)', border: '1px dashed rgba(139,92,246,0.35)' }} data-testid={`subs-${el.elementCode}`}>
                  <div className="flex items-center justify-between px-3 py-2" style={{ borderBottom: '1px dashed rgba(139,92,246,0.25)' }}>
                    <div className="text-[11px] font-semibold" style={{ color: '#8B5CF6' }}>
                      {el.shortName} sub-breakdown · {subs.length} row{subs.length === 1 ? '' : 's'} · Σ {currencySymbol(baseCurrency)}{Math.round(subsBaseTotal).toLocaleString()} / {currencySymbol(baseCurrency)}{Math.round(a.amount || 0).toLocaleString()}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => {
                          // Filter existing Cognara suggestions for this parent element.
                          // If none yet, prompt the user to run "Suggest with Cognara" first.
                          const forParent = (cognaraLevers || []).filter(l => (l.parentCode || (l.code || '').split('.')[0] || '').toUpperCase() === el.elementCode);
                          if (forParent.length === 0) {
                            toast.info(`Click "Suggest with Cognara" above first — no ${el.shortName} sub-levers yet`);
                            return;
                          }
                          // Normalise shares so the sub-breakdown sums to the parent amount.
                          const totalShare = forParent.reduce((s, l) => s + (Number(l.share) || 0), 0) || 1;
                          const parentAmt = Number(a.amount) || 0;
                          const nextSubs = forParent.map((l, idx) => {
                            const pct = Math.round((l.share / totalShare) * 100 * 10) / 10;
                            const base = Math.round(parentAmt * (l.share / totalShare));
                            return {
                              id: `sub_${el.elementCode}_${idx}_${Date.now()}`,
                              name: l.name || l.code,
                              currency: baseCurrency,
                              foreignAmount: base,
                              baseAmount: base,
                              pct,
                            };
                          });
                          setAllocs(prev => ({ ...prev, [el.elementCode]: { ...(prev[el.elementCode] || {}), subs: nextSubs } }));
                          toast.success(`Cognara suggested ${nextSubs.length} ${el.shortName} sub-rows`);
                        }}
                        className="text-[10px] px-2 py-1 rounded font-semibold"
                        style={{ backgroundColor: '#8B5CF6', color: '#fff' }}
                        data-testid={`suggest-subs-${el.elementCode}`}
                      >
                        <Wand2 className="w-3 h-3 inline mr-1" />Cognara Breakdown
                      </button>
                      <button
                        onClick={() => setAllocs(prev => ({ ...prev, [el.elementCode]: { ...(prev[el.elementCode] || {}), subs: [...(prev[el.elementCode]?.subs || []), { id: `sub_${el.elementCode}_${Date.now()}`, name: '', currency: baseCurrency, foreignAmount: 0, baseAmount: 0, pct: 0 }] } }))}
                        className="text-[10px] px-2 py-1 rounded font-semibold"
                        style={{ backgroundColor: 'var(--card-bg)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                        data-testid={`add-sub-${el.elementCode}`}
                      >
                        <Plus className="w-3 h-3 inline mr-0.5" />Add row
                      </button>
                    </div>
                  </div>
                  {subs.length > 0 && (
                    <div className="p-2">
                      <div className="grid grid-cols-[1fr_100px_140px_80px_90px_28px] gap-2 text-[9px] font-semibold uppercase tracking-wider mb-1 px-1" style={{ color: 'var(--text-disabled)' }}>
                        <div>Name</div>
                        <div className="text-center">Currency</div>
                        <div className="text-right">Amount (foreign)</div>
                        <div className="text-right">% of {el.shortName}</div>
                        <div className="text-right">Base {baseCurrency}</div>
                        <div />
                      </div>
                      {subs.map((s, idx) => (
                        <div key={s.id} className="grid grid-cols-[1fr_100px_140px_80px_90px_28px] gap-2 items-center py-1 px-1" data-testid={`sub-row-${el.elementCode}-${idx}`}>
                          <input
                            type="text" value={s.name}
                            onChange={e => setAllocs(prev => {
                              const next = { ...prev };
                              const arr = [...(next[el.elementCode]?.subs || [])];
                              arr[idx] = { ...arr[idx], name: e.target.value };
                              next[el.elementCode] = { ...next[el.elementCode], subs: arr };
                              return next;
                            })}
                            placeholder="e.g. Skilled labour"
                            className="text-xs rounded px-2 py-1 w-full"
                            style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                          />
                          <select
                            value={s.currency || baseCurrency}
                            onChange={e => setAllocs(prev => {
                              const next = { ...prev };
                              const arr = [...(next[el.elementCode]?.subs || [])];
                              const ccy = e.target.value;
                              const rate = ccy === baseCurrency ? 1 : (fxRates?.[ccy] || 0);
                              const baseAmount = rate > 0 ? Math.round((Number(arr[idx].foreignAmount) || 0) / (ccy === baseCurrency ? 1 : rate)) : (arr[idx].baseAmount || 0);
                              const parentAmt = Number(next[el.elementCode].amount) || 0;
                              const pct = parentAmt > 0 ? Math.round((baseAmount / parentAmt) * 100 * 10) / 10 : 0;
                              arr[idx] = { ...arr[idx], currency: ccy, baseAmount, pct };
                              next[el.elementCode] = { ...next[el.elementCode], subs: arr };
                              return next;
                            })}
                            className="text-xs rounded px-1 py-1 text-center font-mono"
                            style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                          >
                            {SUPPORTED_CCY.map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                          <input
                            type="number" min="0" value={s.foreignAmount || ''}
                            onChange={e => setAllocs(prev => {
                              const next = { ...prev };
                              const arr = [...(next[el.elementCode]?.subs || [])];
                              const foreign = parseFloat(e.target.value) || 0;
                              const ccy = arr[idx].currency || baseCurrency;
                              const rate = ccy === baseCurrency ? 1 : (fxRates?.[ccy] || 0);
                              const baseAmount = rate > 0 ? Math.round(foreign / (ccy === baseCurrency ? 1 : rate)) : 0;
                              const parentAmt = Number(next[el.elementCode].amount) || 0;
                              const pct = parentAmt > 0 ? Math.round((baseAmount / parentAmt) * 100 * 10) / 10 : 0;
                              arr[idx] = { ...arr[idx], foreignAmount: foreign, baseAmount, pct };
                              next[el.elementCode] = { ...next[el.elementCode], subs: arr };
                              return next;
                            })}
                            placeholder={`${currencySymbol(s.currency || baseCurrency)} 0`}
                            className="text-right text-xs font-mono rounded px-2 py-1 w-full"
                            style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                          />
                          <input
                            type="number" min="0" max="100" step="0.1"
                            value={s.pct ? s.pct.toFixed(1) : ''}
                            onChange={e => setAllocs(prev => {
                              const next = { ...prev };
                              const arr = [...(next[el.elementCode]?.subs || [])];
                              const pctVal = parseFloat(e.target.value) || 0;
                              // Cap pct at 100 minus other sub rows.
                              const otherPct = arr.reduce((s, r, i) => i === idx ? s : s + (Number(r.pct) || 0), 0);
                              const capped = Math.max(0, Math.min(pctVal, Math.max(0, 100 - otherPct)));
                              const parentAmt = Number(next[el.elementCode].amount) || 0;
                              const baseAmount = Math.round(parentAmt * (capped / 100));
                              const ccy = arr[idx].currency || baseCurrency;
                              const rate = ccy === baseCurrency ? 1 : (fxRates?.[ccy] || 1);
                              const foreign = Math.round(baseAmount * (ccy === baseCurrency ? 1 : rate));
                              arr[idx] = { ...arr[idx], pct: capped, baseAmount, foreignAmount: foreign };
                              next[el.elementCode] = { ...next[el.elementCode], subs: arr };
                              return next;
                            })}
                            className="text-right text-xs font-mono rounded px-2 py-1 w-full"
                            style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                          />
                          <div className="text-right text-xs font-mono" style={{ color: 'var(--text-secondary)' }}>
                            {currencySymbol(baseCurrency)}{Math.round(s.baseAmount || 0).toLocaleString()}
                          </div>
                          <button
                            onClick={() => setAllocs(prev => {
                              const next = { ...prev };
                              const arr = (next[el.elementCode]?.subs || []).filter((_, i) => i !== idx);
                              next[el.elementCode] = { ...next[el.elementCode], subs: arr };
                              return next;
                            })}
                            className="text-red-500 hover:text-red-600 justify-self-end"
                            data-testid={`del-sub-${el.elementCode}-${idx}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      {Math.abs(subsBaseTotal - (Number(a.amount) || 0)) >= 1 && (
                        <div className="mt-1 px-1 text-[10px] text-amber-500">
                          Sub-total {currencySymbol(baseCurrency)}{Math.round(subsBaseTotal).toLocaleString()} ≠ parent {currencySymbol(baseCurrency)}{Math.round(a.amount || 0).toLocaleString()} — adjust rows or use Cognara Breakdown
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            );
          })}
        </div>

        {/* Stacked distribution preview bar */}
        <div className="px-6 py-2" style={{ borderTop: '1px solid var(--border-color)' }}>
          <div className="h-4 rounded-full overflow-hidden flex" style={{ backgroundColor: 'var(--border-color)' }}>
            {displayElements.map(el => {
              const a = allocs[el.elementCode] || {};
              const pct = budget > 0 ? ((a.amount || 0) / budget) * 100 : 0;
              if (pct <= 0) return null;
              return (
                <div key={el.elementCode} className="h-full transition-all duration-200 relative group"
                  style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: el.displayColor }}
                  title={`${el.shortName}: ${pct.toFixed(1)}%`}>
                  {pct > 6 && <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-white">{pct.toFixed(0)}%</span>}
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            {displayElements.map(el => {
              const a = allocs[el.elementCode] || {};
              if ((a.amount || 0) <= 0) return null;
              return (
                <div key={el.elementCode} className="flex items-center gap-1 text-[10px]" style={{ color: 'var(--text-muted)' }}>
                  <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: el.displayColor }} />
                  {el.shortName} {((a.amount || 0) / budget * 100).toFixed(1)}%
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer with validation + submit */}
        <div className="px-6 py-4 flex items-center justify-between" style={{ borderTop: '1px solid var(--border-color)', backgroundColor: 'var(--table-header-bg)' }}>
          <div className="flex items-center gap-4">
            {/* Validation badge */}
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold ${isBalanced ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-500'}`}
              data-testid="validation-badge">
              {isBalanced ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
              {isBalanced ? 'Balanced' : `${difference > 0 ? 'Over' : 'Under'} by ${fmtINR(Math.abs(Math.round(difference)))}`}
            </div>
            <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
              Allocated: <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{fmtINR(Math.round(totalAllocated))}</span>
              <span className="mx-1">/</span>
              Budget: <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{fmtINR(budget)}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onClose} data-testid="wizard-cancel-btn">Cancel</Button>
            <Button size="sm" onClick={handleSubmit} disabled={!isBalanced || submitting}
              className="px-5"
              style={{ backgroundColor: isBalanced ? '#6366F1' : undefined, color: isBalanced ? '#fff' : undefined }}
              data-testid="wizard-save-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              Save Distribution
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   MAIN CBS MATRIX PAGE
   ═══════════════════════════════════════════════════════ */
export default function CBSMatrix() {
  const { projectId: ctxProjectId } = useProject();
  const navigate = useNavigate();
  const gridRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [matrixData, setMatrixData] = useState(null);
  const [distribution, setDistribution] = useState(null);
  const [viewMode, setViewMode] = useState('variance');
  // ── Cognara "Overall Budget → per-leaf distribution" workflow ──
  const [overallBudget, setOverallBudget] = useState(0);
  // Project base currency + live FX rates. Fetched once per project switch.
  // When a planner enters procurement/actuals in a foreign currency (e.g.
  // $500 for imported catalyst), we convert to the project base via `fxRates`.
  const [baseCurrency, setBaseCurrency] = useState('INR');
  const [fxRates, setFxRates] = useState({});           // { USD:0.012, EUR:..., ... } — 1 base = N foreign
  const [fxUpdatedAt, setFxUpdatedAt] = useState(null); // ISO ts of last upstream pull
  const [overallDistributing, setOverallDistributing] = useState(false);
  const [overallProposal, setOverallProposal] = useState([]);
  const [overallReviewOpen, setOverallReviewOpen] = useState(false);
  const [overallCommitting, setOverallCommitting] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [activeTab, setActiveTab] = useState('matrix');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardRow, setWizardRow] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set()); // costCodes that are expanded
  const [collapseAllTick, setCollapseAllTick] = useState(0); // used to seed defaults after fetch
  // Modal states for Sync / Import / Add WBS
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [syncMode, setSyncMode] = useState('overwrite'); // 'overwrite' | 'merge'
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importMode, setImportMode] = useState('append');   // 'replace' | 'append'
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newCode, setNewCode] = useState({ costCode: '', costCodeName: '', originalBudget: 0, costCategory: 'ENGINEERING', costType: 'DIRECT', parentCostCode: '' });
  const [addingCode, setAddingCode] = useState(false);
  const [viewDropdownOpen, setViewDropdownOpen] = useState(false);

  // Schedule Link modal (manual pick + Cognara HITL recommend)
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkRow, setLinkRow] = useState(null);
  const [linkTaskIds, setLinkTaskIds] = useState([]);        // int uids selected in the modal
  const [linkSaving, setLinkSaving] = useState(false);
  const [linkTasksAll, setLinkTasksAll] = useState([]);      // {uid, wbs_code, name, is_critical}
  const [linkTaskSearch, setLinkTaskSearch] = useState('');
  const [linkShowLinkedOnly, setLinkShowLinkedOnly] = useState(false);  // when opened from a chip with existing links, default to only-linked view
  // Cognara HITL
  const [cognaraOpen, setCognaraOpen] = useState(false);
  const [cognaraLoading, setCognaraLoading] = useState(false);
  const [cognaraProposals, setCognaraProposals] = useState([]);
  const [cognaraNote, setCognaraNote] = useState(null);
  const [cognaraStats, setCognaraStats] = useState({ totalUnlinked: 0, coveredByProposals: 0 });
  const [cognaraAccepted, setCognaraAccepted] = useState({});  // {costCode: Set(uid)}

  // ── Multi-select "Apply Template to Many" workflow ─────────────────────
  // We track selected LEAF cost codes (grand-total and summary rows are
  // ignored) plus which shared Cognara template the planner wants to fan
  // out. Selection is sibling-scoped: every checked leaf must share the
  // same `parentCostCode`, otherwise the toolbar shows a warning and
  // disables the apply button.
  const [selectedLeafCodes, setSelectedLeafCodes] = useState([]);
  const [selectedLeafParent, setSelectedLeafParent] = useState(null);
  const [applyManyOpen, setApplyManyOpen] = useState(false);
  const [applyManyTemplates, setApplyManyTemplates] = useState([]);
  const [applyManyTemplateId, setApplyManyTemplateId] = useState('');
  const [applyManyBusy, setApplyManyBusy] = useState(false);

  // Toggle a single node's expanded state.
  const toggleExpand = useCallback((costCode) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(costCode)) next.delete(costCode);
      else next.add(costCode);
      return next;
    });
  }, []);

  const fetchProjects = useCallback(async () => {
    try {
      const { data } = await api.get('/v1/cc/projects-with-data');
      setProjects(data.projects || []);
      if (data.projects?.length > 0 && !selectedProject) {
        setSelectedProject(data.projects[0].project_id);
      }
    } catch { toast.error('Failed to load projects'); }
  }, [selectedProject]);

  const fetchMatrix = useCallback(async () => {
    if (!selectedProject) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/v1/projects/${selectedProject}/cc/cbs/matrix?view=${viewMode}`);
      setMatrixData(data);
      const distRes = await api.get(`/v1/projects/${selectedProject}/cc/cbs/analytics/resource-distribution`);
      setDistribution(distRes.data.distribution || []);
    } catch (err) {
      toast.error('Failed to load CBS matrix');
    } finally { setLoading(false); }
  }, [selectedProject, viewMode]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  // Load project base currency + live FX rates whenever the project changes.
  // FX rates are cached server-side (60 min TTL) so this is cheap on the
  // hot path. `rates.USD = 0.012` means 1 base = 0.012 USD.
  useEffect(() => {
    if (!selectedProject) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/v1/projects/${selectedProject}/cc/currency`);
        if (!cancelled) setBaseCurrency((data.baseCurrency || 'INR').toUpperCase());
      } catch { if (!cancelled) setBaseCurrency('INR'); }
    })();
    return () => { cancelled = true; };
  }, [selectedProject]);

  useEffect(() => {
    if (!baseCurrency) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/fx/rates?base=${baseCurrency}`);
        if (!cancelled) { setFxRates(data.rates || {}); setFxUpdatedAt(data.updatedAt); }
      } catch { if (!cancelled) { setFxRates({}); setFxUpdatedAt(null); } }
    })();
    return () => { cancelled = true; };
  }, [baseCurrency]);

  // Persist a base-currency change server-side. Fire-and-forget so the UI
  // switches instantly and reconciles on next mount.
  const changeBaseCurrency = useCallback(async (ccy) => {
    setBaseCurrency(ccy);
    try {
      await api.put(`/v1/projects/${selectedProject}/cc/currency`, { baseCurrency: ccy });
      toast.success(`Base currency set to ${ccy}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Currency update failed'); }
  }, [selectedProject]);

  const [syncingSSOT, setSyncingSSOT] = useState(false);

  const syncFromSchedule = useCallback(async (mode) => {
    if (!selectedProject) return;
    setSyncingSSOT(true);
    try {
      const { data } = await api.post(`/v1/projects/${selectedProject}/cc/cbs/sync-from-schedule`, {
        strategy: mode || 'overwrite',
        default_element_type: 'DIRECT_COST',
      });
      toast.success(`Imported ${data.imported} activities into CBS`);
      setSyncModalOpen(false);
      fetchMatrix();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed — ensure the project has a linked macro schedule');
    } finally { setSyncingSSOT(false); }
  }, [selectedProject, fetchMatrix]);

  const exportExcel = useCallback(async () => {
    if (!selectedProject) return;
    try {
      const res = await api.get(`/v1/projects/${selectedProject}/cc/cost-codes/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `CBS_${selectedProject}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CBS exported to Excel');
    } catch (e) {
      toast.error('Export failed');
    }
  }, [selectedProject]);

  const importExcel = useCallback(async () => {
    if (!selectedProject || !importFile) return;
    setImporting(true);
    try {
      const form = new FormData();
      form.append('file', importFile);
      form.append('mode', importMode);
      const { data } = await api.post(`/v1/projects/${selectedProject}/cc/cost-codes/import`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Imported: ${data.inserted} new · ${data.updated} updated${data.errors?.length ? ` · ${data.errors.length} errors` : ''}`);
      setImportModalOpen(false);
      setImportFile(null);
      fetchMatrix();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
    } finally { setImporting(false); }
  }, [selectedProject, importFile, importMode, fetchMatrix]);

  const downloadTemplate = useCallback(async () => {
    if (!selectedProject) return;
    try {
      const res = await api.get(`/v1/projects/${selectedProject}/cc/cost-codes/template`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `CBS_TEMPLATE_${selectedProject}.xlsx`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded — includes 6 sample rows + Instructions sheet');
    } catch { toast.error('Template download failed'); }
  }, [selectedProject]);

  // ── Schedule-Link workflow (manual + Cognara HITL) ─────────────────
  const openLinkModal = useCallback(async (row) => {
    if (!row || row.level === -1) return;
    setLinkRow(row);
    // Preserve numeric uids as numbers; keep non-numeric ids (task.id strings) as-is.
    const existing = (row.linkedScheduleTaskIds || [])
      .map(x => {
        if (typeof x === 'number' && Number.isFinite(x)) return x;
        const n = Number(x);
        return Number.isFinite(n) ? n : String(x);
      })
      .filter(v => v !== '' && v !== null && v !== undefined && !(typeof v === 'string' && v.toLowerCase() === 'none') && !(typeof v === 'number' && !Number.isFinite(v)));
    setLinkTaskIds(existing);
    setLinkTaskSearch('');
    setLinkShowLinkedOnly(existing.length > 0);  // if row already has links, default to showing only those
    setLinkModalOpen(true);
    // Lazy-load the macro-schedule task list once per portfolio project.
    if (linkTasksAll.length === 0 && selectedProject) {
      try {
        // Preferred: resolve the linked schedule project via mtxLinkedResources
        // (that's the source of truth for CBS↔Schedule linkage; the
        // scheduling_projects.portfolio_project_id field is not always set).
        let sched_pid = null;
        try {
          const { data: link } = await api.get(`/v1/projects/${selectedProject}/mtx/linked-resources`);
          sched_pid = link?.scheduleProjectId || null;
        } catch (_) { /* fall through to /scheduling/projects lookup */ }

        // Fallback: match by portfolio_project_id in the scheduling projects list.
        if (!sched_pid) {
          const spResp = await api.get(`/scheduling/projects`);
          const spList = Array.isArray(spResp.data) ? spResp.data : (spResp.data?.projects || []);
          const sp = spList.find(x => x.portfolio_project_id === selectedProject);
          sched_pid = sp?.id || null;
        }

        if (!sched_pid) { toast.warning('No macro schedule linked to this project yet.'); return; }
        const tr = await api.get(`/scheduling/projects/${sched_pid}/tasks`);
        const tasks = Array.isArray(tr.data) ? tr.data : (tr.data?.tasks || []);
        setLinkTasksAll(tasks.filter(t => !t.is_summary));
      } catch {
        toast.error('Could not load macro schedule tasks.');
      }
    }
  }, [selectedProject, linkTasksAll.length]);

  const saveScheduleLinks = useCallback(async () => {
    if (!selectedProject || !linkRow) return;
    setLinkSaving(true);
    try {
      await api.put(
        `/v1/projects/${selectedProject}/cc/cost-codes/${encodeURIComponent(linkRow.costCode)}/schedule-links`,
        { taskIds: linkTaskIds },
      );
      toast.success(`Linked ${linkTaskIds.length} activit${linkTaskIds.length === 1 ? 'y' : 'ies'} to ${linkRow.costCode}`);
      setLinkModalOpen(false); setLinkRow(null);
      fetchMatrix();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setLinkSaving(false); }
  }, [selectedProject, linkRow, linkTaskIds, fetchMatrix]);

  const runCognaraRecommend = useCallback(async (useLLM = false) => {
    if (!selectedProject) return;
    setCognaraLoading(true); setCognaraOpen(true);
    try {
      const { data } = await api.post(
        `/v1/projects/${selectedProject}/cc/cbs/recommend-schedule-links`,
        { onlyUnlinked: true, minConfidence: 0.35, useCognara: !!useLLM },
      );
      setCognaraProposals(data.proposals || []);
      setCognaraNote(data.cognaraNote || null);
      setCognaraStats({ totalUnlinked: data.totalUnlinked || 0, coveredByProposals: data.coveredByProposals || 0 });
      // Pre-accept the single-best suggestion per row so the user only tweaks outliers.
      const acc = {};
      (data.proposals || []).forEach(p => {
        const best = (p.suggestions || [])[0];
        if (best) acc[p.costCode] = new Set([best.taskUid]);
      });
      setCognaraAccepted(acc);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cognara recommendation failed');
      setCognaraOpen(false);
    } finally { setCognaraLoading(false); }
  }, [selectedProject]);

  const commitCognaraLinks = useCallback(async () => {
    if (!selectedProject) return;
    const links = Object.entries(cognaraAccepted)
      .map(([costCode, set]) => ({ costCode, taskIds: Array.from(set) }))
      .filter(l => l.taskIds.length > 0);
    if (links.length === 0) { toast.warning('Nothing selected'); return; }
    try {
      const { data } = await api.post(`/v1/projects/${selectedProject}/cc/cbs/apply-schedule-links`, { links });
      toast.success(`Applied ${data.applied} of ${data.requested} link${data.requested === 1 ? '' : 's'}`);
      setCognaraOpen(false); setCognaraProposals([]); setCognaraAccepted({});
      fetchMatrix();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Commit failed');
    }
  }, [selectedProject, cognaraAccepted, fetchMatrix]);

  const addCostCode = useCallback(async () => {
    if (!selectedProject || !newCode.costCode.trim() || !newCode.costCodeName.trim()) {
      toast.error('Cost Code and Description are required');
      return;
    }
    setAddingCode(true);
    try {
      await api.post(`/v1/projects/${selectedProject}/cc/cost-codes`, {
        costCode: newCode.costCode.trim(),
        costCodeName: newCode.costCodeName.trim(),
        parentCostCode: newCode.parentCostCode.trim() || null,
        costCategory: newCode.costCategory,
        costType: newCode.costType,
        originalBudget: Number(newCode.originalBudget) || 0,
      });
      toast.success(`WBS "${newCode.costCode}" created`);
      setAddModalOpen(false);
      setNewCode({ costCode: '', costCodeName: '', originalBudget: 0, costCategory: 'ENGINEERING', costType: 'DIRECT', parentCostCode: '' });
      fetchMatrix();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add cost code');
    } finally { setAddingCode(false); }
  }, [selectedProject, newCode, fetchMatrix]);

  useEffect(() => {
    if (selectedProject) {
      fetchMatrix();
      // Reset cached macro-schedule tasks so switching portfolios re-fetches
      // the correct scheduling project (mtxLinkedResources per portfolio).
      setLinkTasksAll([]);
    }
  }, [selectedProject, fetchMatrix]);

  const openWizardForRow = useCallback((row) => {
    if (!row || row.level === -1) return; // skip GRAND TOTAL
    if (row._hasChildren) {
      toast.info('Select a leaf cost code to distribute budget');
      return;
    }
    setWizardRow(row);
    setWizardOpen(true);
  }, []);

  // Cell renderer for the Cost Code column — draws indent + expand chevron.
  const CostCodeCell = useCallback((p) => {
    const d = p.data || {};
    const lvl = d.level || 0;
    const hasKids = !!d._hasChildren;
    const isOpen = !!d._expanded;
    if (d.level === -1) return <span style={{ fontWeight: 800 }}>{d.costCode}</span>;
    return (
      <div className="flex items-center h-full" style={{ paddingLeft: `${lvl * 16}px`, gap: 4 }}>
        {hasKids ? (
          <button
            type="button"
            data-cbs-chevron="1"
            onClick={(e) => {
              e.stopPropagation();
              e.nativeEvent?.stopImmediatePropagation?.();
              toggleExpand(d.costCode);
            }}
            className="p-0.5 rounded hover:bg-black/10 transition-colors flex items-center justify-center"
            data-testid={`cbs-toggle-${d.costCode}`}
            style={{ width: 16, height: 16 }}
          >
            {isOpen
              ? <ChevronDown className="w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />
              : <ChevronRight className="w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />}
          </button>
        ) : (
          <span style={{ width: 16, display: 'inline-block' }} />
        )}
        <span style={{ fontWeight: hasKids ? 700 : 400 }}>{d.costCode}</span>
      </div>
    );
  }, [toggleExpand]);

  const columnDefs = useMemo(() => {
    if (!matrixData) return [];
    const cols = [
      {
        headerName: 'Cost Code', field: 'costCode', pinned: 'left', width: 200,
        cellRenderer: CostCodeCell,
        cellStyle: p => ({ padding: 0, cursor: p.data?._hasChildren ? 'pointer' : 'default' }),
        sortable: false,
        // Checkbox selection appears ONLY on leaf rows so summary / grand-total
        // rows can never be pulled into the "Apply Template to Many" fan-out.
        checkboxSelection: (p) => !!p.data && !p.data._hasChildren && p.data.level !== -1,
      },
      {
        headerName: 'Description', field: 'costCodeName', pinned: 'left', width: 260,
        cellStyle: p => ({ fontWeight: p.data?._hasChildren ? '700' : '400', cursor: p.data?._hasChildren ? 'pointer' : 'default' })
      },
      {
        headerName: 'Sched Link', field: 'linkedScheduleTaskCount', pinned: 'left', width: 140,
        cellRenderer: (p) => {
          const d = p.data || {};
          if (d.level === -1 || d._hasChildren) return null;
          const count = d.linkedScheduleTaskCount || (d.linkedScheduleTaskIds || []).length || 0;
          const dates = d.linkedScheduleDates || {};
          const fmt = (v) => v ? new Date(v).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: '2-digit' }) : '—';
          const title = count > 0
            ? `${count} activit${count === 1 ? 'y' : 'ies'} linked\n` +
              `Plan: ${fmt(dates.plannedStart)} → ${fmt(dates.plannedFinish)}\n` +
              `Fcst: ${fmt(dates.forecastStart)} → ${fmt(dates.forecastFinish)}\n` +
              `Actl: ${fmt(dates.actualStart)} → ${fmt(dates.actualFinish)}` +
              (dates.taskNames?.length ? `\n\nTasks: ${dates.taskNames.slice(0, 5).join(', ')}` : '') +
              `\n\nClick to edit.`
            : 'Click to link macro-schedule activities.';
          return (
            <div
              onClick={(e) => { e.stopPropagation(); openLinkModal(d); }}
              className="flex flex-col items-start gap-0.5 h-full cursor-pointer justify-center"
              data-testid={`cbs-link-cell-${d.costCode}`}
              title={title}>
              {count > 0 ? (
                <>
                  <span className="px-1.5 py-0.5 rounded font-mono text-[10px] font-semibold"
                    style={{ background: '#22C55E22', color: '#16A34A', border: '1px solid #22C55E55' }}>
                    {count} linked
                  </span>
                  {(dates.plannedStart || dates.forecastStart) && (
                    <span className="text-[9px] font-mono truncate" style={{ color: 'var(--text-muted)' }}>
                      {fmt(dates.forecastStart || dates.plannedStart)} → {fmt(dates.forecastFinish || dates.plannedFinish)}
                    </span>
                  )}
                </>
              ) : (
                <span className="px-1.5 py-0.5 rounded font-mono text-[10px] font-semibold"
                  style={{ background: 'var(--card-bg)', color: 'var(--text-disabled)', border: '1px dashed var(--border-color)' }}>
                  + Link
                </span>
              )}
            </div>
          );
        },
        cellStyle: () => ({ padding: '2px 4px' }),
        sortable: false,
      },
    ];   /* eslint-disable-next-line no-unused-vars */
    // NB: openLinkModal captured via closure — deps list below includes it.
    for (const el of matrixData.elements) {
      const subCols = [];
      if (viewMode === 'variance' || viewMode === 'budget') {
        subCols.push({
          headerName: 'Budget', field: `cell_${el.elementCode}_budget`, width: 100, type: 'numericColumn',
          valueFormatter: p => fmt(p.value),
          cellStyle: p => ({ color: 'var(--text-primary)', fontWeight: p.data?.level === 0 ? '600' : '400', textAlign: 'right' })
        });
      }
      if (viewMode === 'variance' || viewMode === 'actual') {
        subCols.push({
          headerName: 'Actual', field: `cell_${el.elementCode}_actual`, width: 100, type: 'numericColumn',
          valueFormatter: p => fmt(p.value),
          cellStyle: () => ({ textAlign: 'right' })
        });
      }
      if (viewMode === 'variance') {
        subCols.push({
          headerName: 'Variance', field: `cell_${el.elementCode}_variance`, width: 100, type: 'numericColumn',
          valueFormatter: p => fmt(p.value),
          cellStyle: p => ({ color: (p.value || 0) >= 0 ? '#22C55E' : '#EF4444', fontWeight: '600', textAlign: 'right' })
        });
      }
      if (viewMode === 'forecast') {
        subCols.push(
          { headerName: 'Budget', field: `cell_${el.elementCode}_budget`, width: 95, type: 'numericColumn', valueFormatter: p => fmt(p.value), cellStyle: () => ({ textAlign: 'right' }) },
          { headerName: 'EAC', field: `cell_${el.elementCode}_eac`, width: 95, type: 'numericColumn', valueFormatter: p => fmt(p.value), cellStyle: () => ({ textAlign: 'right' }) },
          { headerName: 'VAC', field: `cell_${el.elementCode}_vac`, width: 95, type: 'numericColumn', valueFormatter: p => fmt(p.value), cellStyle: p => ({ color: (p.value || 0) >= 0 ? '#22C55E' : '#EF4444', fontWeight: '600', textAlign: 'right' }) }
        );
      }
      if (viewMode === 'commitment') {
        subCols.push(
          { headerName: 'Budget', field: `cell_${el.elementCode}_budget`, width: 95, type: 'numericColumn', valueFormatter: p => fmt(p.value), cellStyle: () => ({ textAlign: 'right' }) },
          { headerName: 'Committed', field: `cell_${el.elementCode}_committed`, width: 100, type: 'numericColumn', valueFormatter: p => fmt(p.value), cellStyle: () => ({ textAlign: 'right' }) },
          { headerName: 'Uncommitted', field: `cell_${el.elementCode}_uncommitted`, width: 100, type: 'numericColumn', valueFormatter: p => fmt(p.value), cellStyle: p => ({ color: (p.value || 0) >= 0 ? '#22C55E' : '#EF4444', textAlign: 'right' }) }
        );
      }
      cols.push({ headerName: el.shortName, headerClass: 'ag-header-group-element', children: subCols, marryChildren: true });
    }
    const totalSubs = [
      { headerName: 'Budget', field: 'totalBudget', width: 110, type: 'numericColumn', valueFormatter: p => fmt(p.value), cellStyle: () => ({ fontWeight: '700', textAlign: 'right', color: 'var(--text-primary)' }) }
    ];
    if (viewMode === 'variance' || viewMode === 'actual') {
      totalSubs.push({ headerName: 'Actual', field: 'totalActual', width: 110, type: 'numericColumn', valueFormatter: p => fmt(p.value), cellStyle: () => ({ fontWeight: '700', textAlign: 'right' }) });
    }
    if (viewMode === 'variance') {
      totalSubs.push({ headerName: 'Variance', field: 'totalVariance', width: 110, type: 'numericColumn', valueFormatter: p => fmt(p.value), cellStyle: p => ({ fontWeight: '700', color: (p.value || 0) >= 0 ? '#22C55E' : '#EF4444', textAlign: 'right' }) });
    }
    cols.push({ headerName: 'TOTAL', children: totalSubs, marryChildren: true });
    return cols;
  }, [matrixData, viewMode]);

  // Auto-expand root level (level 0) whenever new matrix data lands, so users
  // see the top of the hierarchy immediately (same UX as macro schedule).
  useEffect(() => {
    if (!matrixData) return;
    const roots = new Set(
      matrixData.rows.filter(r => (r.level || 0) === 0).map(r => r.costCode)
    );
    setExpanded(roots);
  }, [matrixData, collapseAllTick]);

  // Build a tree from parentCostCode, roll up parent totals from children,
  // then flatten in natural (root → descendants) order and filter by expansion.
  const { rowData, pinnedBottomRowData } = useMemo(() => {
    if (!matrixData) return { rowData: [], pinnedBottomRowData: [] };

    const elements = matrixData.elements;
    const byCode = {};
    matrixData.rows.forEach(r => {
      byCode[r.costCode] = {
        costCode: r.costCode,
        costCodeName: r.costCodeName,
        level: r.level || 0,
        parentCostCode: r.parentCostCode || null,
        costCategory: r.costCategory,
        currentBudget: r.currentBudget,
        linkedScheduleTaskIds: r.linkedScheduleTaskIds || [],
        linkedScheduleTaskCount: r.linkedScheduleTaskCount ?? (r.linkedScheduleTaskIds || []).length,
        linkedScheduleDates: r.linkedScheduleDates || {},
        totalBudget: r.totalBudget || 0,
        totalActual: r.totalActual || 0,
        totalVariance: r.totalVariance || 0,
        totalEAC: r.totalEAC || 0,
        totalCommitted: r.totalCommitted || 0,
        cells: { ...(r.cells || {}) },
        _children: [],
      };
    });
    const roots = [];
    Object.values(byCode).forEach(n => {
      const p = n.parentCostCode && byCode[n.parentCostCode];
      if (p) p._children.push(n);
      else roots.push(n);
    });

    // Roll up: parent totals are always the sum of their descendant leaves
    // (SSOT). This way, once budget is distributed at leaf cost codes, every
    // ancestor summary row shows the aggregated cost automatically.
    const sumField = (node, field) =>
      node._children.reduce((s, c) => s + (c[field] || 0), 0);
    const rollup = (node) => {
      node._children.forEach(rollup);
      if (!node._children.length) return;
      node.totalBudget = sumField(node, 'totalBudget');
      node.totalActual = sumField(node, 'totalActual');
      node.totalVariance = node.totalBudget - node.totalActual;
      node.totalEAC = sumField(node, 'totalEAC');
      node.totalCommitted = sumField(node, 'totalCommitted');
      elements.forEach(el => {
        const ec = el.elementCode;
        const kids = node._children.map(c => c.cells?.[ec]).filter(Boolean);
        if (kids.length) {
          const b = kids.reduce((s, c) => s + (c.budget || 0), 0);
          const a = kids.reduce((s, c) => s + (c.actual || 0), 0);
          const e = kids.reduce((s, c) => s + (c.eac || 0), 0);
          const cm = kids.reduce((s, c) => s + (c.committed || 0), 0);
          node.cells[ec] = {
            budget: b, actual: a, variance: b - a,
            eac: e, vac: b - e,
            committed: cm, uncommitted: b - cm,
            status: 'GREEN',
          };
        } else {
          // No leaf cell data for this element under this parent — zero it out
          // so a stale cell doesn't override the roll-up.
          delete node.cells[ec];
        }
      });
    };
    roots.forEach(rollup);

    // Flatten depth-first in natural order (roots already sorted by backend).
    const flat = [];
    const walk = (n) => { flat.push(n); n._children.forEach(walk); };
    roots.forEach(walk);

    // Filter to visible (all ancestors expanded).
    const isVisible = (n) => {
      let p = n.parentCostCode;
      while (p) {
        if (!expanded.has(p)) return false;
        p = byCode[p]?.parentCostCode;
      }
      return true;
    };

    const rows = flat.filter(isVisible).map(n => {
      const row = {
        costCode: n.costCode,
        costCodeName: n.costCodeName,
        level: n.level,
        parentCostCode: n.parentCostCode,
        costCategory: n.costCategory,
        currentBudget: n.currentBudget,
        totalBudget: n.totalBudget,
        totalActual: n.totalActual,
        totalVariance: n.totalVariance,
        totalEAC: n.totalEAC,
        totalCommitted: n.totalCommitted,
        linkedScheduleTaskIds: n.linkedScheduleTaskIds || [],
        linkedScheduleTaskCount: n.linkedScheduleTaskCount ?? (n.linkedScheduleTaskIds || []).length,
        linkedScheduleDates: n.linkedScheduleDates || {},
        cells: n.cells,
        _hasChildren: n._children.length > 0,
        _expanded: expanded.has(n.costCode),
      };
      for (const el of elements) {
        const cell = n.cells[el.elementCode];
        if (cell) {
          row[`cell_${el.elementCode}_budget`] = cell.budget;
          row[`cell_${el.elementCode}_actual`] = cell.actual;
          row[`cell_${el.elementCode}_variance`] = cell.variance;
          row[`cell_${el.elementCode}_eac`] = cell.eac;
          row[`cell_${el.elementCode}_vac`] = cell.vac;
          row[`cell_${el.elementCode}_committed`] = cell.committed;
          row[`cell_${el.elementCode}_uncommitted`] = cell.uncommitted;
          row[`cell_${el.elementCode}_status`] = cell.status;
        }
      }
      return row;
    });

    // Grand total pinned row — recompute from rolled-up roots.
    const totalRow = {
      costCode: '', costCodeName: 'GRAND TOTAL', level: -1,
      totalBudget: roots.reduce((s, n) => s + (n.totalBudget || 0), 0),
      totalActual: roots.reduce((s, n) => s + (n.totalActual || 0), 0),
      totalEAC: roots.reduce((s, n) => s + (n.totalEAC || 0), 0),
      totalCommitted: roots.reduce((s, n) => s + (n.totalCommitted || 0), 0),
    };
    totalRow.totalVariance = totalRow.totalBudget - totalRow.totalActual;
    for (const el of elements) {
      const ec = el.elementCode;
      const b = roots.reduce((s, n) => s + (n.cells?.[ec]?.budget || 0), 0);
      const a = roots.reduce((s, n) => s + (n.cells?.[ec]?.actual || 0), 0);
      const e = roots.reduce((s, n) => s + (n.cells?.[ec]?.eac || 0), 0);
      const cm = roots.reduce((s, n) => s + (n.cells?.[ec]?.committed || 0), 0);
      totalRow[`cell_${ec}_budget`] = b;
      totalRow[`cell_${ec}_actual`] = a;
      totalRow[`cell_${ec}_variance`] = b - a;
      totalRow[`cell_${ec}_eac`] = e;
      totalRow[`cell_${ec}_vac`] = b - e;
      totalRow[`cell_${ec}_committed`] = cm;
      totalRow[`cell_${ec}_uncommitted`] = b - cm;
    }
    return { rowData: rows, pinnedBottomRowData: [totalRow] };
  }, [matrixData, expanded]);



  const defaultColDef = useMemo(() => ({
    sortable: true, resizable: true, suppressMovable: true,
    cellStyle: { fontSize: '12px' },
    headerClass: 'ag-header-cell-small'
  }), []);

  const getRowStyle = useCallback((params) => {
    if (params.node.rowPinned) return { fontWeight: '800', backgroundColor: 'var(--table-header-bg)' };
    const lvl = params.data?.level || 0;
    if (params.data?._hasChildren) {
      // Tint summary rows slightly by depth so the hierarchy pops visually.
      const shades = ['rgba(99,102,241,0.06)', 'rgba(99,102,241,0.04)', 'rgba(99,102,241,0.025)'];
      return { backgroundColor: shades[Math.min(lvl, shades.length - 1)], fontWeight: '600' };
    }
    return {};
  }, []);

  const onRowClicked = useCallback((e) => {
    // Ignore the row-click when the user actually clicked the chevron button —
    // otherwise the row handler would toggle the state a second time and the
    // net effect would be "nothing happened" (broken collapse).
    const target = e.event?.target;
    if (target && target.closest && target.closest('[data-cbs-chevron="1"]')) return;
    if (e.data?._hasChildren) toggleExpand(e.data.costCode);
  }, [toggleExpand]);

  const onRowDoubleClicked = useCallback((e) => {
    // Only open the wizard for leaf rows — parents just toggle.
    if (e.data && !e.data._hasChildren) openWizardForRow(e.data);
  }, [openWizardForRow]);

  // ── AG-Grid selection handler ─────────────────────────────────────────
  // Feeds `selectedLeafCodes` + `selectedLeafParent`. Enforces the
  // "same-parent" rule: if the planner picks rows across different
  // WBS branches we still record them but the toolbar disables the
  // apply-many button.
  const onSelectionChanged = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    const rows = api.getSelectedRows().filter((r) => r && !r._hasChildren && r.level !== -1);
    setSelectedLeafCodes(rows.map((r) => r.costCode));
    const parents = new Set(rows.map((r) => r.parentCostCode || '__root__'));
    setSelectedLeafParent(parents.size === 1 ? [...parents][0] : null);
  }, []);

  // Fetch shared Cognara templates for the current project when the
  // "Apply-many" modal is opened.
  useEffect(() => {
    if (!applyManyOpen || !selectedProject) return;
    api.get(`/v1/projects/${selectedProject}/cc/cbs/lever-templates`)
      .then(({ data }) => {
        setApplyManyTemplates(data.templates || []);
        setApplyManyTemplateId((data.templates || [])[0]?.id || '');
      })
      .catch(() => { setApplyManyTemplates([]); setApplyManyTemplateId(''); });
  }, [applyManyOpen, selectedProject]);

  const handleApplyTemplateMany = async () => {
    if (!applyManyTemplateId || selectedLeafCodes.length === 0) return;
    setApplyManyBusy(true);
    try {
      const { data } = await api.post(
        `/v1/projects/${selectedProject}/cc/cbs/apply-template-many`,
        { templateId: applyManyTemplateId, costCodes: selectedLeafCodes },
      );
      const appliedCount = data?.applied || 0;
      const skippedCount = (data?.skipped || []).length;
      toast.success(
        `Template applied to ${appliedCount} activities${skippedCount ? ` · ${skippedCount} skipped (no budget)` : ''}`,
      );
      setApplyManyOpen(false);
      gridRef.current?.api?.deselectAll();
      setSelectedLeafCodes([]);
      setSelectedLeafParent(null);
      fetchMatrix();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apply-many failed');
    } finally { setApplyManyBusy(false); }
  };

  const expandAll = useCallback(() => {
    if (!matrixData) return;
    setExpanded(new Set(matrixData.rows.map(r => r.costCode)));
  }, [matrixData]);

  const collapseAll = useCallback(() => {
    setExpanded(new Set());
  }, []);

  const currentView = VIEW_MODES.find(v => v.id === viewMode);
  const ViewIcon = currentView?.icon || Target;

  const donutData = distribution?.map(d => ({ name: d.shortName, value: d.budget, color: d.displayColor, actual: d.actual })) || [];
  const varianceData = distribution?.map(d => ({ name: d.shortName, variance: d.variance, color: d.displayColor, budget: d.budget, actual: d.actual })) || [];

  return (
    <>
    <DashboardLayout>
      {ctxProjectId && <AIAnalystPanel projectId={ctxProjectId} moduleKey="CBS_MATRIX" defaultOpen={true} compact={true} />}

      <div className="p-6 space-y-5" data-testid="cbs-matrix-page">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate('/project-controls')} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div className="p-2.5 rounded-xl shrink-0" style={{ background: '#6366F112', border: '1px solid #6366F120' }}>
              <Grid3X3 className="w-5 h-5" style={{ color: '#6366F1' }} />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg font-bold whitespace-nowrap" style={{ color: 'var(--text-primary)' }}>CBS Matrix</h1>
              <p className="text-xs whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>Cost Code x Cost Element — Two-Dimensional Cost Control</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 justify-end">
            <select className="text-sm rounded-lg px-3 py-1.5"
              style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
              value={selectedProject || ''} onChange={e => setSelectedProject(e.target.value)}
              data-testid="project-selector">
              {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
            </select>
            {/* Overall-budget input + Cognara Distribute (human-in-the-loop
                review). Sits in the CBS toolbar so planners can go from
                zero to a full CBS in three clicks: enter budget → Cognara
                proposes per-leaf splits → Approve. */}
            <input
              type="number"
              placeholder={`Overall Budget ${currencySymbol(baseCurrency)}`}
              value={overallBudget || ''}
              onChange={(e) => setOverallBudget(parseFloat(e.target.value) || 0)}
              min={0}
              className="text-sm rounded-lg px-3 py-1.5 w-40 font-mono"
              style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
              data-testid="overall-budget-input"
              title="Type the overall project budget, then click 'Cognara Distribute' to propose a per-leaf split"
            />
            {/* Project base-currency selector — persists on `projects.metadata.baseCurrency`
                and drives KPI labels + FX conversion for foreign-currency procurement. */}
            <select
              className="text-sm rounded-lg px-2 py-1.5 font-mono"
              style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
              value={baseCurrency}
              onChange={(e) => changeBaseCurrency(e.target.value)}
              data-testid="base-currency-select"
              title={fxUpdatedAt ? `FX rates updated ${new Date(fxUpdatedAt).toLocaleString()}` : 'Live FX rates'}
            >
              {SUPPORTED_CCY.map((c) => (
                <option key={c} value={c}>{currencySymbol(c)} {c}</option>
              ))}
            </select>
            <Button variant="outline" size="sm" data-testid="cognara-distribute-btn"
              disabled={!overallBudget || overallDistributing}
              onClick={async () => {
                setOverallDistributing(true);
                try {
                  const { data } = await api.post(
                    `/v1/projects/${selectedProject}/cc/cbs/distribute-overall`,
                    { overallBudget, commit: false },
                  );
                  setOverallProposal(data.proposal || []);
                  setOverallReviewOpen(true);
                } catch (e) { toast.error(e?.response?.data?.detail || 'Cognara proposal failed'); }
                finally { setOverallDistributing(false); }
              }}>
              <Wand2 className="w-3.5 h-3.5 mr-1" style={{ color: '#8B5CF6' }} />
              {overallDistributing ? 'Proposing…' : 'Cognara Distribute'}
            </Button>
            <div className="relative">
              <Button variant="outline" size="sm" onClick={() => setViewDropdownOpen(!viewDropdownOpen)}
                data-testid="view-mode-dropdown">
                <ViewIcon className="w-4 h-4 mr-1" /> {currentView?.label} <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
              {viewDropdownOpen && (
                <div className="absolute right-0 top-full mt-1 rounded-lg shadow-lg z-50 w-52 py-1"
                  style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                  {VIEW_MODES.map(v => (
                    <button key={v.id}
                      className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:opacity-80"
                      style={{ color: v.id === viewMode ? '#6366F1' : 'var(--text-primary)', backgroundColor: v.id === viewMode ? '#6366F112' : 'transparent' }}
                      onClick={() => { setViewMode(v.id); setViewDropdownOpen(false); }}>
                      <v.icon className="w-4 h-4" /> {v.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={expandAll} data-testid="cbs-expand-all-btn" title="Expand all">
              <ChevronDown className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={collapseAll} data-testid="cbs-collapse-all-btn" title="Collapse all">
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setAddModalOpen(true)} disabled={!selectedProject}
              data-testid="cbs-add-wbs-btn" title="Add WBS element">
              <Plus className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={exportExcel} disabled={!selectedProject}
              data-testid="cbs-export-xlsx-btn" title="Export to Excel">
              <Download className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={downloadTemplate} disabled={!selectedProject}
              data-testid="cbs-template-btn" title="Download the CBS import template (with sample rows + Instructions)">
              <FileSpreadsheet className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setImportModalOpen(true)} disabled={!selectedProject}
              data-testid="cbs-import-xlsx-btn" title="Import from Excel">
              <Upload className="w-4 h-4" />
            </Button>
            <Button variant="default" size="sm" onClick={() => runCognaraRecommend(false)} disabled={!selectedProject}
              className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white gap-1" data-testid="cbs-cognara-link-btn"
              title="Cognara suggests CBS↔Macro-Schedule links (human-in-the-loop review before commit)">
              <Wand2 className="w-3.5 h-3.5" />
              <span className="text-xs">Link with Cognara</span>
            </Button>
            <Button variant="default" size="sm" onClick={() => setSyncModalOpen(true)} disabled={syncingSSOT || !selectedProject}
              className="bg-purple-600 hover:bg-purple-700 text-white gap-1" data-testid="cbs-sync-from-schedule-btn">
              {syncingSSOT ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
              <span className="text-xs">Sync from Schedule</span>
            </Button>
            <Button variant="outline" size="sm" onClick={fetchMatrix} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--text-muted)' }} />
          </div>
        ) : matrixData ? (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-5 gap-3">
              <KPICard title="Total Budget" value={`${currencySymbol(baseCurrency)}${fmt(matrixData.grandTotal.budget)}`} icon={DollarSign} color="#3B82F6" subtitle={fxUpdatedAt ? `Base: ${baseCurrency} · FX ${new Date(fxUpdatedAt).toLocaleDateString(CURRENCY_LOCALE[baseCurrency] || 'en-IN')}` : `Base: ${baseCurrency}`} />
              <KPICard title="Total Actual" value={`${currencySymbol(baseCurrency)}${fmt(matrixData.grandTotal.actual)}`} icon={Eye} color="#F59E0B" />
              <KPICard title="Total Variance" value={`${currencySymbol(baseCurrency)}${fmt(matrixData.grandTotal.variance)}`} icon={Target} color={matrixData.grandTotal.variance >= 0 ? '#22C55E' : '#EF4444'} />
              <KPICard title="Total Committed" value={`${currencySymbol(baseCurrency)}${fmt(matrixData.grandTotal.committed)}`} icon={BarChart3} color="#143A6B" />
              <KPICard title="Control Cells" value={matrixData.totalCells} subtitle="Active CBS cells" icon={Grid3X3} color="#6366F1" />
            </div>

            {/* Tabs */}
            <div className="flex gap-1 p-0.5 rounded-lg" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
              {[
                { id: 'matrix', label: 'CBS Matrix', icon: Grid3X3 },
                { id: 'analytics', label: 'Resource Analytics', icon: BarChart3 }
              ].map(t => (
                <button key={t.id} onClick={() => setActiveTab(t.id)}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-md transition-all"
                  style={{
                    backgroundColor: activeTab === t.id ? '#6366F115' : 'transparent',
                    color: activeTab === t.id ? '#6366F1' : 'var(--text-muted)',
                    fontWeight: activeTab === t.id ? '600' : '400'
                  }} data-testid={`tab-${t.id}`}>
                  <t.icon className="w-3.5 h-3.5" /> {t.label}
                </button>
              ))}
            </div>

            {activeTab === 'matrix' && (
              <>
                {/* Hint */}
                <div
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs border"
                  style={{ backgroundColor: 'rgba(99,102,241,0.03)', borderColor: 'rgba(99,102,241,0.08)', color: '#6366F1' }}
                >
                  <Info className="w-3.5 h-3.5 shrink-0" />
                  <span>Click a summary row to expand/collapse · Double-click any leaf to open the Distribution Wizard · Tick sibling leaves to fan a Cognara template out to all of them</span>
                </div>

                {/* Multi-select toolbar — appears only when leaf rows are ticked. */}
                {selectedLeafCodes.length > 0 && (
                  <div
                    className="flex items-center justify-between px-3 py-2 rounded-lg text-xs"
                    style={{ backgroundColor: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.25)' }}
                    data-testid="cbs-multiselect-bar"
                  >
                    <div style={{ color: 'var(--text-primary)' }}>
                      <span className="font-semibold" style={{ color: '#8B5CF6' }}>
                        {selectedLeafCodes.length}
                      </span>{' '}
                      leaf activit{selectedLeafCodes.length === 1 ? 'y' : 'ies'} selected
                      {' · '}
                      {selectedLeafParent
                        ? <span style={{ color: 'var(--text-muted)' }}>parent {selectedLeafParent || 'root'}</span>
                        : <span className="text-amber-500">selection spans multiple parents — pick siblings only</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="default"
                        className="bg-purple-600 hover:bg-purple-700 text-white gap-1"
                        disabled={!selectedLeafParent}
                        onClick={() => setApplyManyOpen(true)}
                        data-testid="cbs-apply-template-many-btn"
                      >
                        <Wand2 className="w-3.5 h-3.5" /> Apply Template to Many
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          gridRef.current?.api?.deselectAll();
                          setSelectedLeafCodes([]);
                          setSelectedLeafParent(null);
                        }}
                        data-testid="cbs-clear-selection-btn"
                      >
                        <X className="w-3.5 h-3.5" /> Clear
                      </Button>
                    </div>
                  </div>
                )}
                <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                  <div className="ag-theme-alpine" style={{ height: 580, width: '100%' }} data-testid="cbs-grid">
                    <AgGridReact
                      ref={gridRef}
                      columnDefs={columnDefs}
                      rowData={rowData}
                      defaultColDef={defaultColDef}
                      pinnedBottomRowData={pinnedBottomRowData}
                      getRowStyle={getRowStyle}
                      onRowClicked={onRowClicked}
                      onRowDoubleClicked={onRowDoubleClicked}
                      onSelectionChanged={onSelectionChanged}
                      rowSelection="multiple"
                      suppressRowClickSelection={true}
                      animateRows={true}
                      suppressCellFocus={true}
                      headerHeight={36}
                      rowHeight={34}
                      groupHeaderHeight={32}
                    />
                  </div>
                </div>
              </>
            )}

            {activeTab === 'analytics' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                  <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Budget Distribution by Resource Type</h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie data={donutData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                        {donutData.map((d, i) => <Cell key={i} fill={d.color} />)}
                      </Pie>
                      <Tooltip formatter={(v) => fmt(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                  <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Variance by Cost Element</h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={varianceData} layout="vertical" margin={{ left: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                      <XAxis type="number" tickFormatter={fmt} tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={70} />
                      <Tooltip formatter={v => fmt(v)} />
                      <Bar dataKey="variance" radius={[0, 4, 4, 0]}>
                        {varianceData.map((d, i) => <Cell key={i} fill={d.variance >= 0 ? '#22C55E' : '#EF4444'} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="col-span-2 rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                  <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Budget vs Actual by Resource Type</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={distribution?.map(d => ({ name: d.shortName, Budget: d.budget, Actual: d.actual, Committed: d.committed })) || []} margin={{ bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tickFormatter={fmt} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={v => fmt(v)} />
                      <Legend />
                      <Bar dataKey="Budget" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="Actual" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="Committed" fill="#143A6B" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-20" style={{ color: 'var(--text-muted)' }}>No CBS data found. Seed data first.</div>
        )}
      </div>

      {/* Budget Distribution Wizard */}
      <BudgetDistributionWizard
        isOpen={wizardOpen}
        onClose={() => { setWizardOpen(false); setWizardRow(null); }}
        costCodeRow={wizardRow}
        elements={matrixData?.elements || []}
        selectedProject={selectedProject}
        onDistributed={() => { setWizardOpen(false); fetchMatrix(); }}
        baseCurrency={baseCurrency}
        fxRates={fxRates}
      />

      {/* Sync-from-Schedule modal */}
      {syncModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="sync-modal">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSyncModalOpen(false)} />
          <div className="relative w-[520px] rounded-2xl overflow-hidden" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
            <div className="px-6 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border-color)' }}>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg" style={{ background: '#8B5CF615', border: '1px solid #8B5CF630' }}>
                  <Wand2 className="w-5 h-5" style={{ color: '#8B5CF6' }} />
                </div>
                <div>
                  <h2 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>Sync CBS from Macro Schedule</h2>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Choose how existing CBS data should be handled</p>
                </div>
              </div>
              <button onClick={() => setSyncModalOpen(false)} className="p-1.5 rounded-lg hover:opacity-70">
                <X className="w-5 h-5" style={{ color: 'var(--text-muted)' }} />
              </button>
            </div>
            <div className="p-6 space-y-3">
              {[
                { id: 'overwrite', title: 'Replace all CBS', desc: 'Delete every existing WBS row (including any seed data) and rebuild strictly from the schedule. Recommended for a clean SSOT.' },
                { id: 'merge',     title: 'Merge with existing', desc: 'Keep existing WBS rows and upsert the schedule activities on top. Use only when you want both to coexist.' },
              ].map(o => (
                <label key={o.id} className="flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors"
                  style={{
                    border: `1px solid ${syncMode === o.id ? '#8B5CF6' : 'var(--border-color)'}`,
                    backgroundColor: syncMode === o.id ? '#8B5CF608' : 'transparent',
                  }}
                  data-testid={`sync-mode-${o.id}`}>
                  <input type="radio" name="syncMode" value={o.id}
                    checked={syncMode === o.id}
                    onChange={() => setSyncMode(o.id)}
                    className="mt-1 accent-purple-600"
                  />
                  <div>
                    <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{o.title}</div>
                    <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{o.desc}</div>
                  </div>
                </label>
              ))}
            </div>
            <div className="px-6 py-4 flex items-center justify-end gap-2" style={{ borderTop: '1px solid var(--border-color)', backgroundColor: 'var(--table-header-bg)' }}>
              <Button variant="outline" size="sm" onClick={() => setSyncModalOpen(false)}>Cancel</Button>
              <Button size="sm" onClick={() => syncFromSchedule(syncMode)} disabled={syncingSSOT}
                className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="sync-confirm-btn">
                {syncingSSOT ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Wand2 className="w-4 h-4 mr-1" />}
                {syncMode === 'overwrite' ? 'Replace and Sync' : 'Merge and Sync'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Excel Import modal */}
      {importModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="import-modal">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setImportModalOpen(false)} />
          <div className="relative w-[560px] rounded-2xl overflow-hidden" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
            <div className="px-6 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border-color)' }}>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg" style={{ background: '#0EA5E915', border: '1px solid #0EA5E930' }}>
                  <FileSpreadsheet className="w-5 h-5" style={{ color: '#0EA5E9' }} />
                </div>
                <div>
                  <h2 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>Import CBS from Excel</h2>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Upload an .xlsx file exported from this module (or matching its columns)</p>
                </div>
              </div>
              <button onClick={() => setImportModalOpen(false)} className="p-1.5 rounded-lg hover:opacity-70">
                <X className="w-5 h-5" style={{ color: 'var(--text-muted)' }} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-disabled)' }}>Import mode</div>
                <div className="space-y-2">
                  {[
                    { id: 'replace', title: 'Replace all CBS', desc: 'Wipe every existing WBS row before importing the workbook. The uploaded file becomes the single source of truth.' },
                    { id: 'append',  title: 'Append / Upsert',  desc: 'Keep existing WBS rows. Rows in the workbook are added if new, or updated if the cost code already exists.' },
                  ].map(o => (
                    <label key={o.id} className="flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors"
                      style={{
                        border: `1px solid ${importMode === o.id ? '#0EA5E9' : 'var(--border-color)'}`,
                        backgroundColor: importMode === o.id ? '#0EA5E908' : 'transparent',
                      }}
                      data-testid={`import-mode-${o.id}`}>
                      <input type="radio" name="importMode" value={o.id}
                        checked={importMode === o.id}
                        onChange={() => setImportMode(o.id)}
                        className="mt-1 accent-sky-600"
                      />
                      <div>
                        <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{o.title}</div>
                        <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{o.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-disabled)' }}>Excel file (.xlsx)</div>
                <input
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={e => setImportFile(e.target.files?.[0] || null)}
                  className="block w-full text-xs rounded-lg px-2 py-2"
                  style={{ backgroundColor: 'var(--table-header-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                  data-testid="import-file-input"
                />
                {importFile && <div className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>Selected: {importFile.name}</div>}
              </div>
            </div>
            <div className="px-6 py-4 flex items-center justify-end gap-2" style={{ borderTop: '1px solid var(--border-color)', backgroundColor: 'var(--table-header-bg)' }}>
              <Button variant="outline" size="sm" onClick={() => setImportModalOpen(false)}>Cancel</Button>
              <Button size="sm" onClick={importExcel} disabled={!importFile || importing}
                className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="import-confirm-btn">
                {importing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                {importMode === 'replace' ? 'Replace & Import' : 'Append & Import'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add WBS modal */}
      {addModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="add-wbs-modal">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setAddModalOpen(false)} />
          <div className="relative w-[520px] rounded-2xl overflow-hidden" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
            <div className="px-6 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border-color)' }}>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg" style={{ background: '#22C55E15', border: '1px solid #22C55E30' }}>
                  <Plus className="w-5 h-5" style={{ color: '#22C55E' }} />
                </div>
                <div>
                  <h2 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>Add WBS Element</h2>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Manually add a cost code. Parent + level are auto-derived from a dotted code (e.g. 3.1.4).</p>
                </div>
              </div>
              <button onClick={() => setAddModalOpen(false)} className="p-1.5 rounded-lg hover:opacity-70">
                <X className="w-5 h-5" style={{ color: 'var(--text-muted)' }} />
              </button>
            </div>
            <div className="p-6 grid grid-cols-2 gap-3">
              <div className="col-span-1">
                <label className="text-[10px] uppercase font-semibold tracking-wider" style={{ color: 'var(--text-disabled)' }}>Cost Code *</label>
                <input type="text" value={newCode.costCode}
                  onChange={e => setNewCode(v => ({ ...v, costCode: e.target.value }))}
                  placeholder="e.g. 3.1.4"
                  className="mt-1 w-full text-sm rounded-lg px-2 py-1.5"
                  style={{ backgroundColor: 'var(--table-header-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                  data-testid="new-wbs-code"
                />
              </div>
              <div className="col-span-1">
                <label className="text-[10px] uppercase font-semibold tracking-wider" style={{ color: 'var(--text-disabled)' }}>Parent (optional)</label>
                <input type="text" value={newCode.parentCostCode}
                  onChange={e => setNewCode(v => ({ ...v, parentCostCode: e.target.value }))}
                  placeholder="Auto-derived if empty"
                  className="mt-1 w-full text-sm rounded-lg px-2 py-1.5"
                  style={{ backgroundColor: 'var(--table-header-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                  data-testid="new-wbs-parent"
                />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase font-semibold tracking-wider" style={{ color: 'var(--text-disabled)' }}>Description *</label>
                <input type="text" value={newCode.costCodeName}
                  onChange={e => setNewCode(v => ({ ...v, costCodeName: e.target.value }))}
                  placeholder="e.g. Structural Steel Erection"
                  className="mt-1 w-full text-sm rounded-lg px-2 py-1.5"
                  style={{ backgroundColor: 'var(--table-header-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                  data-testid="new-wbs-name"
                />
              </div>
              <div className="col-span-1">
                <label className="text-[10px] uppercase font-semibold tracking-wider" style={{ color: 'var(--text-disabled)' }}>Category</label>
                <select value={newCode.costCategory}
                  onChange={e => setNewCode(v => ({ ...v, costCategory: e.target.value }))}
                  className="mt-1 w-full text-sm rounded-lg px-2 py-1.5"
                  style={{ backgroundColor: 'var(--table-header-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                >
                  {['ENGINEERING','PROCUREMENT','CONSTRUCTION','PRE_COMMISSIONING','PROJECT_MANAGEMENT','CONTINGENCY'].map(o => <option key={o} value={o}>{o.replace(/_/g,' ')}</option>)}
                </select>
              </div>
              <div className="col-span-1">
                <label className="text-[10px] uppercase font-semibold tracking-wider" style={{ color: 'var(--text-disabled)' }}>Original Budget (INR)</label>
                <input type="number" value={newCode.originalBudget}
                  onChange={e => setNewCode(v => ({ ...v, originalBudget: e.target.value }))}
                  placeholder="0"
                  className="mt-1 w-full text-sm rounded-lg px-2 py-1.5"
                  style={{ backgroundColor: 'var(--table-header-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                  data-testid="new-wbs-budget"
                />
              </div>
            </div>
            <div className="px-6 py-4 flex items-center justify-end gap-2" style={{ borderTop: '1px solid var(--border-color)', backgroundColor: 'var(--table-header-bg)' }}>
              <Button variant="outline" size="sm" onClick={() => setAddModalOpen(false)}>Cancel</Button>
              <Button size="sm" onClick={addCostCode} disabled={addingCode}
                className="bg-green-600 hover:bg-green-700 text-white" data-testid="add-wbs-confirm-btn">
                {addingCode ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                Create WBS
              </Button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>

    {/* ═══ Schedule-Link modal (manual pick) ═══ */}
    <Dialog open={linkModalOpen} onOpenChange={(v) => { setLinkModalOpen(v); if (!v) setLinkRow(null); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="cbs-link-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="w-5 h-5" style={{ color: '#0EA5E9' }} />
            Link Macro-Schedule Activities
          </DialogTitle>
        </DialogHeader>
        {linkRow && (
          <div className="text-xs mb-2 flex items-center gap-2" style={{ color: 'var(--text-muted)' }}>
            <span className="font-mono font-semibold px-1.5 py-0.5 rounded" style={{ background: '#0EA5E922', color: '#0EA5E9' }}>{linkRow.costCode}</span>
            <span>{linkRow.costCodeName}</span>
            <span className="ml-auto flex items-center gap-2">
              {linkTaskIds.length > 0 && (
                <label className="flex items-center gap-1 text-[10px] cursor-pointer" data-testid="cbs-link-show-linked-only-toggle">
                  <input type="checkbox" className="accent-sky-600"
                    checked={linkShowLinkedOnly}
                    onChange={e => setLinkShowLinkedOnly(e.target.checked)} />
                  Show linked only ({linkTaskIds.length})
                </label>
              )}
              <span>{linkTaskIds.length} selected</span>
            </span>
          </div>
        )}
        <input
          type="text"
          value={linkTaskSearch}
          onChange={e => setLinkTaskSearch(e.target.value)}
          placeholder="Search tasks by name, WBS code, or uid…"
          className="mb-2 w-full text-sm rounded-lg px-2 py-1.5"
          style={{ backgroundColor: 'var(--table-header-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
          data-testid="cbs-link-search"
        />
        <div className="flex-1 overflow-auto rounded-lg border" style={{ borderColor: 'var(--border-color)' }}>
          {linkTasksAll.length === 0 ? (
            <div className="p-6 text-center text-xs" style={{ color: 'var(--text-disabled)' }}>Loading macro-schedule tasks…</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0" style={{ background: 'var(--card-bg-alt)' }}>
                <tr>
                  <th className="w-8"></th>
                  <th className="text-left p-2">WBS</th>
                  <th className="text-left p-2">Name</th>
                  <th className="text-right p-2">Start</th>
                  <th className="text-right p-2">Finish</th>
                </tr>
              </thead>
              <tbody>
                {linkTasksAll
                  .filter(t => {
                    const tId = t.uid ?? t.id;
                    if (linkShowLinkedOnly && !linkTaskIds.includes(tId)) return false;
                    const q = linkTaskSearch.trim().toLowerCase();
                    if (!q) return true;
                    return (t.name || '').toLowerCase().includes(q)
                      || String(t.wbs_code || '').toLowerCase().includes(q)
                      || String(tId || '').toLowerCase().includes(q);
                  })
                  .slice(0, 500)
                  .map(t => {
                    const tId = t.uid ?? t.id;
                    const checked = linkTaskIds.includes(tId);
                    return (
                      <tr key={tId}
                        className="border-t cursor-pointer hover:bg-black/5"
                        style={{ borderColor: 'var(--border-color)' }}
                        onClick={() => setLinkTaskIds(prev => checked ? prev.filter(x => x !== tId) : [...prev, tId])}
                        data-testid={`cbs-link-task-${tId}`}>
                        <td className="p-2 text-center">
                          <input type="checkbox" checked={checked} readOnly className="accent-sky-600" />
                        </td>
                        <td className="p-2 font-mono text-[10px]">{t.wbs_code || tId}</td>
                        <td className="p-2">
                          <div className="flex items-center gap-1">
                            <span>{t.name}</span>
                            {t.is_critical && <span className="text-[9px] font-bold px-1 rounded" style={{ background: '#DC2626', color: '#fff' }}>CP</span>}
                          </div>
                        </td>
                        <td className="p-2 text-right text-[10px]" style={{ color: 'var(--text-muted)' }}>{t.scheduled_start ? new Date(t.scheduled_start).toLocaleDateString() : '—'}</td>
                        <td className="p-2 text-right text-[10px]" style={{ color: 'var(--text-muted)' }}>{t.scheduled_finish ? new Date(t.scheduled_finish).toLocaleDateString() : '—'}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          )}
        </div>
        <div className="flex items-center justify-between gap-2 mt-3">
          <Button variant="outline" size="sm" onClick={() => runCognaraRecommend(false)}
            data-testid="cbs-link-cognara-btn"
            className="gap-1">
            <Wand2 className="w-3.5 h-3.5" style={{ color: '#A855F7' }} />
            Ask Cognara across all CBS rows
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setLinkTaskIds([])} data-testid="cbs-link-clear">Clear</Button>
            <Button variant="outline" size="sm" onClick={() => setLinkModalOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={saveScheduleLinks} disabled={linkSaving}
              className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="cbs-link-save">
              {linkSaving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              Save Links
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* ═══ Cognara CBS↔Schedule HITL Recommend Dialog ═══ */}
    <Dialog open={cognaraOpen} onOpenChange={setCognaraOpen}>
      <DialogContent className="max-w-4xl max-h-[88vh] flex flex-col" data-testid="cbs-cognara-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5" style={{ color: '#A855F7' }} />
            Cognara — Suggested CBS ↔ Macro-Schedule Links
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs mb-2 flex items-center gap-2 flex-wrap" style={{ color: 'var(--text-muted)' }}>
          <span>{cognaraStats.coveredByProposals} of {cognaraStats.totalUnlinked} unlinked CBS rows have suggestions</span>
          <span>·</span>
          <span>Uncheck any bad match. Only checked links will be committed.</span>
          <Button variant="ghost" size="sm" className="h-6 text-xs ml-auto"
            onClick={() => runCognaraRecommend(true)} disabled={cognaraLoading}
            data-testid="cbs-cognara-refresh-llm">
            <Wand2 className="w-3 h-3 mr-1" /> Ask Cognara to validate
          </Button>
        </div>
        {cognaraNote && (
          <div className="rounded p-2 text-[11px] mb-2"
            style={{ background: '#A855F712', border: '1px solid #A855F740', color: 'var(--text-primary)' }}
            data-testid="cbs-cognara-note">
            <b style={{ color: '#A855F7' }}>Cognara notes:</b> {cognaraNote}
          </div>
        )}
        <div className="flex-1 overflow-auto rounded-lg border" style={{ borderColor: 'var(--border-color)' }}>
          {cognaraLoading ? (
            <div className="p-8 text-center text-xs" style={{ color: 'var(--text-muted)' }}>
              <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" />
              Analyzing WBS vs macro schedule…
            </div>
          ) : cognaraProposals.length === 0 ? (
            <div className="p-8 text-center text-xs" style={{ color: 'var(--text-disabled)' }}>
              No high-confidence matches. Try lowering the threshold or link manually.
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
              {cognaraProposals.map(p => (
                <div key={p.costCode} className="p-3">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="font-mono text-[10px] px-1.5 py-0.5 rounded font-semibold"
                      style={{ background: '#0EA5E922', color: '#0EA5E9' }}>{p.costCode}</span>
                    <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>{p.costCodeName}</span>
                  </div>
                  <div className="space-y-1">
                    {p.suggestions.map(s => {
                      const acc = cognaraAccepted[p.costCode] || new Set();
                      const checked = acc.has(s.taskUid);
                      const conf = s.confidence;
                      const confColor = conf >= 0.75 ? '#22C55E' : conf >= 0.5 ? '#F59E0B' : '#94A3B8';
                      return (
                        <label key={s.taskUid} className="flex items-center gap-2 p-1.5 rounded cursor-pointer text-xs hover:bg-black/5"
                          style={{ border: `1px ${checked ? 'solid' : 'dashed'} ${checked ? confColor : 'var(--border-color)'}`,
                                    background: checked ? `${confColor}10` : 'transparent' }}
                          data-testid={`cbs-cognara-sugg-${p.costCode}-${s.taskUid}`}>
                          <input type="checkbox" checked={checked} className="accent-purple-600"
                            onChange={() => setCognaraAccepted(prev => {
                              const cur = new Set(prev[p.costCode] || []);
                              if (cur.has(s.taskUid)) cur.delete(s.taskUid); else cur.add(s.taskUid);
                              return { ...prev, [p.costCode]: cur };
                            })} />
                          <span className="px-1.5 py-0.5 rounded font-mono text-[9px] font-bold"
                            style={{ background: `${confColor}22`, color: confColor }}>
                            {Math.round(conf * 100)}%
                          </span>
                          <span className="flex-1">
                            <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{s.taskName}</span>
                            {s.isCritical && <span className="ml-1 text-[9px] font-bold px-1 rounded" style={{ background: '#DC2626', color: '#fff' }}>CP</span>}
                            <span className="ml-2 text-[10px]" style={{ color: 'var(--text-muted)' }}>uid {s.taskUid} · {s.wbsCode || ''}</span>
                          </span>
                          <span className="text-[10px] italic" style={{ color: 'var(--text-disabled)' }}>{s.reasoning}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center justify-between gap-2 mt-3">
          <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
            {Object.values(cognaraAccepted).reduce((s, v) => s + (v?.size || 0), 0)} links checked ·
            covers {Object.keys(cognaraAccepted).filter(k => (cognaraAccepted[k]?.size || 0) > 0).length} CBS rows
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setCognaraOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={commitCognaraLinks} disabled={cognaraLoading}
              className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="cbs-cognara-commit">
              <CheckCircle2 className="w-4 h-4 mr-1" />
              Commit selected links
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* ═══ Cognara Overall-Budget Distribution — HITL Review Dialog ═══ */}
    <Dialog open={overallReviewOpen} onOpenChange={setOverallReviewOpen}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="cognara-overall-review-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5" style={{ color: '#8B5CF6' }} /> Cognara Proposed CBS Split
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>
          Overall Budget: <b>₹{Number(overallBudget).toLocaleString('en-IN')}</b> · {overallProposal.length} leaves ·
          Review each row, then approve to commit.
        </div>
        <div className="flex-1 overflow-auto rounded-lg border" style={{ borderColor: 'var(--border-color)' }}>
          <table className="w-full text-xs">
            <thead className="sticky top-0" style={{ background: 'var(--card-bg-alt)' }}>
              <tr>
                <th className="text-left p-2">Cost Code</th>
                <th className="text-left p-2">Name</th>
                <th className="text-left p-2">Category</th>
                <th className="text-right p-2">Share</th>
                <th className="text-right p-2">Proposed Budget</th>
              </tr>
            </thead>
            <tbody>
              {overallProposal.map((p) => (
                <tr key={p.costCode} style={{ borderTop: '1px solid var(--border-color)' }}>
                  <td className="p-2 font-mono">{p.costCode}</td>
                  <td className="p-2">{p.name}</td>
                  <td className="p-2"><Badge variant="outline" className="text-[10px]">{p.category || '—'}</Badge></td>
                  <td className="p-2 text-right font-mono">{(p.share * 100).toFixed(2)}%</td>
                  <td className="p-2 text-right font-mono">₹{Math.round(p.proposedBudget).toLocaleString('en-IN')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <Button variant="outline" onClick={() => setOverallReviewOpen(false)}>Cancel</Button>
          <Button onClick={async () => {
            setOverallCommitting(true);
            try {
              await api.post(`/v1/projects/${selectedProject}/cc/cbs/distribute-overall`, { overallBudget, commit: true });
              toast.success(`Committed ${overallProposal.length} budgets — refresh the matrix to see them`);
              setOverallReviewOpen(false);
            } catch (e) { toast.error(e?.response?.data?.detail || 'Commit failed'); }
            finally { setOverallCommitting(false); }
          }} disabled={overallCommitting} data-testid="cognara-overall-commit-btn">
            {overallCommitting ? 'Committing…' : `Approve & Commit ${overallProposal.length} budgets`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Apply-Template-to-Many Modal */}
    <Dialog open={applyManyOpen} onOpenChange={setApplyManyOpen}>
      <DialogContent className="max-w-lg" data-testid="cbs-apply-many-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5" style={{ color: '#8B5CF6' }} />
            Apply Cognara Template to {selectedLeafCodes.length} Activit{selectedLeafCodes.length === 1 ? 'y' : 'ies'}
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
          The template's Cognara lever mix is applied to each selected leaf using that leaf's
          own <b>Current Budget</b>. Rows without a budget are skipped — set a budget first,
          then re-run.
        </p>
        <div className="mt-3">
          <label className="text-xs uppercase font-semibold tracking-wider" style={{ color: 'var(--text-muted)' }}>
            Pick a saved template
          </label>
          {applyManyTemplates.length === 0 ? (
            <div className="mt-2 text-xs p-3 rounded" style={{ background: 'var(--card-bg-alt)', color: 'var(--text-muted)' }}>
              No shared templates yet. Open one leaf, run "Suggest with Cognara", then click
              "Save as Template" — the mix will appear here.
            </div>
          ) : (
            <select
              value={applyManyTemplateId}
              onChange={(e) => setApplyManyTemplateId(e.target.value)}
              className="w-full mt-1 px-2 py-2 rounded text-sm"
              style={{ background: 'var(--card-bg-alt)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
              data-testid="cbs-apply-many-template-select"
            >
              {applyManyTemplates.map((t) => (
                <option key={t.id} value={t.id}>{t.name} — {t.category}</option>
              ))}
            </select>
          )}
        </div>
        <div className="max-h-32 overflow-auto mt-3 rounded border" style={{ borderColor: 'var(--border-color)' }}>
          <table className="w-full text-[11px]">
            <thead style={{ background: 'var(--card-bg-alt)' }}>
              <tr>
                <th className="text-left p-1.5">Cost Code</th>
                <th className="text-left p-1.5">Parent</th>
              </tr>
            </thead>
            <tbody>
              {selectedLeafCodes.map((c) => (
                <tr key={c} style={{ borderTop: '1px solid var(--border-color)' }}>
                  <td className="p-1.5 font-mono">{c}</td>
                  <td className="p-1.5 font-mono" style={{ color: 'var(--text-muted)' }}>{selectedLeafParent || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <Button variant="outline" onClick={() => setApplyManyOpen(false)}>Cancel</Button>
          <Button
            className="bg-purple-600 hover:bg-purple-700 text-white gap-1"
            onClick={handleApplyTemplateMany}
            disabled={applyManyBusy || !applyManyTemplateId || selectedLeafCodes.length === 0}
            data-testid="cbs-apply-many-confirm-btn"
          >
            {applyManyBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
            Apply to {selectedLeafCodes.length}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  </>
  );
}
